import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import * as XLSX from 'xlsx';
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { saveFileWithPrompt, downloadUrlWithPrompt } from "../../../shared/utils/downloadUtils";
import moment from "moment";
import { Nav } from "react-bootstrap";
import Select from 'react-select';

import {
    clientsList,
    sitesList,
    plansAvailable,
    plansCurrent,
    projectsList,
    estimationRunRatesSummaryJson,
    estimationRunCostSummaryJson,
    estimationRunRatesSummary,
    estimationRunCostSummary,
    foundationEstimationResult,
    gfBeamEstimationResult,
    gfColumnEstimationResult,
    gfSlabEstimationResult,
    lintelBeamEstimationResult,
    plinthEstimationResult,
    projectAdd,
    projectDetail,
    projectEstimation,
    runCpm,
    runStaircaseEstimationResult,
    wfdEstimationResult,
    projectRatesGet,
    projectRatesSave,
    projectRatesReset,
    materialRatesGet,
    materialRatesSave,
    labourRatesGet,
    labourRatesSave,
    planDetail
} from "../../../shared/settings/apiConfig";
import { getApi, postApi, putApi, patchApi } from "../../../shared/services/apiHelper";
import { formatINR } from "../../../utils/formatCurrency";
import { useDispatch } from 'react-redux';
import { actions } from '../../../redux/actions';

import useLocalStoragePersist from "../../../shared/hooks/useLocalStoragePersist";
import { validateProjectCreation, getMaxFloorsAllowed, getAllowedBuildingTypes } from "../../../shared/utils/planValidator";
import "./ProjectOnboard.scss";

// Custom styles for react-select dropdowns (mobile-friendly)
const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        background: '#ffffff',
        borderColor: state.isFocused ? '#0032CD' : '#e2e8f0',
        borderWidth: '2px',
        borderRadius: '10px',
        padding: '6px 8px',
        fontSize: '15px',
        fontWeight: '500',
        color: '#1e293b',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(0, 50, 205, 0.1)' : 'none',
        '&:hover': {
            borderColor: '#0032CD'
        },
        minHeight: '47px'
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: '10px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        zIndex: 9999
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999
    }),
    menuList: (provided) => ({
        ...provided,
        padding: '4px',
        maxHeight: '300px'
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#0032CD' : state.isFocused ? '#f8fafc' : 'transparent',
        color: state.isSelected ? '#ffffff' : '#1e293b',
        padding: '12px 16px',
        fontSize: '15px',
        fontWeight: state.isSelected ? '600' : '500',
        cursor: 'pointer',
        borderRadius: '6px',
        margin: '2px 0',
        '&:active': {
            backgroundColor: '#0032CD'
        }
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#94a3b8',
        fontSize: '15px'
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#1e293b',
        fontSize: '15px',
        fontWeight: '500'
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        color: '#64748b',
        '&:hover': {
            color: '#0032CD'
        }
    }),
    indicatorSeparator: () => ({
        display: 'none'
    })
};

/**
 * Safely extract an error message string from any error shape.
 * FastAPI/Pydantic validation errors return detail as an array of {type, loc, msg, input, ctx}
 * which cannot be rendered directly in React.
 */
const safeErrorMessage = (error, fallback = "An unexpected error occurred.") => {
    if (!error) return fallback;
    const detail = error?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail.map(err => err.msg || JSON.stringify(err)).join("; ");
    }
    if (typeof detail === "object" && detail !== null) {
        return detail.msg || detail.message || JSON.stringify(detail);
    }
    return String(detail);
};
const drawingTypes = [
    { id: "foundation", title: "Foundation", description: "Foundation and footing concrete reinforcement structure." },
    { id: "plinth-beam", title: "Plinth Beam", description: "Ground tie-beam layouts and reinforcement details." },
    { id: "floor-beam", title: "Floor Beam", description: "Slab support beams and structural bending plans." },
    { id: "floor-slab", title: "Floor Slab", description: "Reinforcement, depth dimensions, and concrete grade." },
    { id: "columns", title: "Columns", description: "Vertical column design, steel schedule, and coordinates." },
    { id: "superstructure", title: "Plan and Elevation", description: "Internal rooms, partitions, and architectural layout plans." },
    // { id: "elevation", title: "Elevation", description: "External front, side, and rear perspective elevations." },
    { id: "lintel-beam", title: "Lintel Beam", description: "Lintel beam concrete, ties, and details." },
    { id: "staircase", title: "Staircase", description: "Staircase layouts, concrete waist slabs, and tread reinforcement." }
];

const uploadSteps = [
    { id: "foundation", title: "Foundation", displayId: "foundation" },
    { id: "plinth-beam", title: "Plinth Beam", displayId: "plinth_beam" },
    { id: "floor-beam", title: "Floor Beam", displayId: "floor_beam" },
    { id: "floor-slab", title: "Floor Slab", displayId: "floor_slab" },
    { id: "columns", title: "Columns", displayId: "columns" },
    { id: "superstructure", title: "Plan", displayId: "superstructure" },
    // { id: "elevation", title: "Elevation", displayId: "elevation" },
    { id: "lintel-beam", title: "Lintel Beam", displayId: "lintel_beam" },
    { id: "staircase", title: "Staircase", displayId: "staircase" }
];

const moduleApiConfig = {
    foundation: { module: "foundation", api: foundationEstimationResult, floorAware: false },
    "plinth-beam": { module: "plinth_beam", api: plinthEstimationResult, floorAware: false },
    "floor-beam": { module: "gfb", api: gfBeamEstimationResult, floorAware: true },
    "floor-slab": { module: "gfslab", api: gfSlabEstimationResult, floorAware: true },
    columns: { module: "column", api: gfColumnEstimationResult, floorAware: true },
    superstructure: { module: "wfd", api: wfdEstimationResult, floorAware: true },
    // elevation: { module: "wfd", api: wfdEstimationResult, floorAware: true },
    "lintel-beam": { module: "lb", api: lintelBeamEstimationResult, floorAware: true },
    staircase: { module: "staircase", api: runStaircaseEstimationResult, floorAware: true }
};

const iconPath = {
    foundation: "M4 20h16M6 20V8l6-4 6 4v12M9 20v-7h6v7",
    "plinth-beam": "M4 8h16M4 16h16M6 5v14M12 5v14M18 5v14",
    "floor-beam": "M4 7h16v8H4zM8 7v8M16 7v8",
    "floor-slab": "M4 6h16M4 10h16M4 14h16M4 18h16M8 4v16M16 4v16",
    columns: "M6 3h4v18H6zM14 3h4v18h-4z",
    superstructure: "M4 20V9l8-5 8 5v11M8 20v-6h8v6",
    // elevation: "M3 12l9-9 9 9M5 10v10h14V10",
    "lintel-beam": "M4 9h16M5 15h14M8 5v14M16 5v14",
    staircase: "M20 6h-4v4h-4v4H8v4H4"
};

const floorNames = [
    "Ground Floor",
    "First Floor",
    "Second Floor",
    "Third Floor",
    "Fourth Floor",
    "Fifth Floor",
    "Sixth Floor",
    "Seventh Floor",
    "Eighth Floor",
    "Ninth Floor"
];

// Stages shown while the upload + AI estimation is running.
// `min` is the uploadProgress threshold at which the stage becomes active.
const estimationStages = [
    { id: "prepare", label: "Preparing files", min: 0 },
    { id: "convert", label: "Converting images", min: 15 },
    { id: "upload", label: "Uploading drawings", min: 30 },
    { id: "ai", label: "AI analysing drawings", min: 60 },
    { id: "results", label: "Building results", min: 85 },
    { id: "done", label: "Complete", min: 100 }
];

// Honest "what happens during estimation" copy shown while the AI stage runs.
const estimationWaitingTips = [
    "The AI is reading structural grids, spans and dimensions from your drawing.",
    "Beam and column sizes are being detected along with reinforcement details.",
    "Quantities are converted into BOQ items using standard civil take-off rules.",
    "Tip: you can override any rate later in the Cost tab once results are ready.",
    "Tip: results are saved per drawing, so you can re-run other drawings anytime.",
    "Tip: clear, high-resolution drawings with visible dimensions give the best accuracy."
];

const formatElapsed = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
        const result = reader.result || "";
        resolve(String(result).replace(/^data:.*;base64,/, ""));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const formatLabel = (value) => String(value || "")
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, char => char.toUpperCase());

const flattenResultRows = (data, parentKey = "") => {
    if (!data) return [];

    // Handle array of metrics with label/quantity/unit structure (e.g., plinth beam results)
    if (Array.isArray(data) && data.length > 0 && data[0] && typeof data[0] === "object" && "label" in data[0]) {
        return data.map(metric => ({
            item: metric.label || metric.item || "Unknown",
            quantity: metric.quantity ?? metric.qty ?? metric.value ?? "-",
            unit: metric.unit ?? metric.units ?? "-"
        }));
    }

    if (Array.isArray(data)) {
        return data.flatMap((item, index) => {
            if (item && typeof item === "object") {
                return flattenResultRows(item, parentKey);
            }
            return [{
                item: parentKey || `Item ${index + 1}`,
                quantity: item,
                unit: "-"
            }];
        });
    }

    if (typeof data !== "object") {
        return [{ item: parentKey || "Result", quantity: data, unit: "-" }];
    }

    return Object.entries(data).flatMap(([key, value]) => {
        const label = parentKey ? `${parentKey} / ${formatLabel(key)}` : formatLabel(key);

        if (value && typeof value === "object") {
            if (!Array.isArray(value) && ("quantity" in value || "qty" in value || "value" in value || "total" in value)) {
                return [{
                    item: label,
                    quantity: value.quantity ?? value.qty ?? value.value ?? value.total ?? "-",
                    unit: value.unit ?? value.units ?? "-"
                }];
            }
            return flattenResultRows(value, label);
        }

        return [{
            item: label,
            quantity: value ?? "-",
            unit: "-"
        }];
    });
};

/**
 * Recalculate costSummary rows using custom rates.
 * Each data row (non-banner, non-subtotal, non-grand-total) gets its rate replaced
 * from the ratesMap (key = Item name) and amount recomputed = Quantity * custom rate.
 * Banner/subtotal/total rows are kept as-is from the original cost summary structure.
 */
const recalcCostWithCustomRates = (originalSummary, ratesMap) => {
    if (!originalSummary || !originalSummary.rows) return originalSummary;

    const newRows = originalSummary.rows.map(row => {
        // Only modify actual data rows (type === 'item')
        if (row.type && row.type !== 'item') {
            // Keep floor/module banners, subtotals, totals as-is
            return { ...row };
        }

        const newRow = { ...row };
        const rateKey = row.Item || '';

        if (ratesMap[rateKey] !== undefined && ratesMap[rateKey] !== null) {
            const customRate = Number(ratesMap[rateKey]);
            newRow["Rate (₹)"] = customRate;
            const qty = Number(row.Quantity) || 0;
            newRow["Amount (₹)"] = qty * customRate;
        }
        return newRow;
    });

    // Recalculate subtotals, floor totals, grand total
    let currentModuleItems = [];
    let currentFloorItems = [];
    let grandTotal = 0;
    const recalculated = [];
    let currentModuleName = '';
    let currentFloorName = '';

    for (const row of newRows) {
        if (row.type === 'floor') {
            // Flush floor totals
            if (currentFloorItems.length > 0 && currentFloorName) {
                const floorSum = currentFloorItems.reduce((sum, r) => sum + (Number(r["Amount (₹)"]) || 0), 0);
                recalculated.push({ Item: "Floor Total", type: "floor_total", "Amount (₹)": floorSum });
            }
            currentFloorName = row["Floor / Module"];
            currentFloorItems = [];
            recalculated.push({ ...row });
        } else if (row.type === 'module') {
            // Flush previous module subtotal
            if (currentModuleItems.length > 0 && currentModuleName) {
                const moduleSum = currentModuleItems.reduce((sum, r) => sum + (Number(r["Amount (₹)"]) || 0), 0);
                recalculated.push({ Item: "Sub Total", type: "module_subtotal", "Amount (₹)": moduleSum });
            }
            currentModuleName = row["Floor / Module"];
            currentModuleItems = [];
            recalculated.push({ ...row });
        } else if (row.type === 'module_subtotal' || row.type === 'floor_total' || row.type === 'grand_total') {
            // Skip original totals - we'll recalculate
            continue;
        } else {
            recalculated.push(row);
            currentModuleItems.push(row);
            currentFloorItems.push(row);
        }
    }

    // Flush last module subtotal
    if (currentModuleItems.length > 0 && currentModuleName) {
        const moduleSum = currentModuleItems.reduce((sum, r) => sum + (Number(r["Amount (₹)"]) || 0), 0);
        recalculated.push({ Item: "Sub Total", type: "module_subtotal", "Amount (₹)": moduleSum });
    }

    // Flush last floor total
    if (currentFloorItems.length > 0 && currentFloorName) {
        const floorSum = currentFloorItems.reduce((sum, r) => sum + (Number(r["Amount (₹)"]) || 0), 0);
        recalculated.push({ Item: "Floor Total", type: "floor_total", "Amount (₹)": floorSum });
    }

    // Grand total
    grandTotal = recalculated
        .filter(r => r.type === 'floor_total')
        .reduce((sum, r) => sum + (Number(r["Amount (₹)"]) || 0), 0);
    recalculated.push({ Item: "Grand Total", type: "grand_total", "Amount (₹)": grandTotal });

    return { ...originalSummary, rows: recalculated };
};

export default function ProjectOnboard(props) {
    const dispatch = useDispatch();
    const history = useHistory();
    
    // Extract project ID from URL params for edit mode
    const projectIdFromUrl = props.match?.params?.id;
    const isEditMode = Boolean(projectIdFromUrl);
    
    // ---------- Session Persistence ----------
    const [getPersisted, setPersisted, clearPersisted] = useLocalStoragePersist();
    const initialRestoreDone = useRef(false);
    const backendDataLoaded = useRef(false);
    const localStorageRestored = useRef

    const [clients, setClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(true);
    const [sites, setSites] = useState([]);
    const [loadingSites, setLoadingSites] = useState(true);
    const [selectedSiteId, setSelectedSiteId] = useState("");
    const [subscriptions, setSubscriptions] = useState([]);
    const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState("");
    const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState(null);
    const [loadingPlanDetails, setLoadingPlanDetails] = useState(false);
    const [planLimits, setPlanLimits] = useState({
        max_estimations: null,
        max_floors: null,
        allowed_building_types: [],
        allow_new_project: true
    });
    const [subscriptionUsage, setSubscriptionUsage] = useState({
        used_count: 0,
        available_count: 0,
        total_count: 0
    });
    const [planName, setPlanName] = useState("");
    const [projectCount, setProjectCount] = useState(0);
    const [buildingType, setBuildingType] = useState("INDEPENDENT");
    const [projectType, setProjectType] = useState(""); // RESIDENTIAL or COMMERCIAL
    const [projectName, setProjectName] = useState("");
    const [selectedClientId, setSelectedClientId] = useState("");
    const [floorsCount, setFloorsCount] = useState(1);
    const [selectedDrawingTypeIds, setSelectedDrawingTypeIds] = useState([]);
    const [currentStep, setCurrentStep] = useState(1);
    const [currentFloorIndex, setCurrentFloorIndex] = useState(1);
    const [stepState, setStepState] = useState("upload");
    const [moduleFiles, setModuleFiles] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [errorStatus, setErrorStatus] = useState("");
    const errorRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [projectId, setProjectId] = useState("");
    const [moduleResults, setModuleResults] = useState({});
    const [costSummary, setCostSummary] = useState(null);
    const [calculatingCost, setCalculatingCost] = useState(false);
    const [activeTab, setActiveTab] = useState('takeoff');
    const [searchQuery, setSearchQuery] = useState('');
    const [saveSuccessMessage, setSaveSuccessMessage] = useState(null);
    // ---------- Custom Cost state ----------
    const [costTab, setCostTab] = useState('custom'); // 'custom' | 'material' | 'labour'
    const [customRates, setCustomRates] = useState({});
    // Store raw input strings so user can type decimals properly
    const [rateInputStrings, setRateInputStrings] = useState({});
    const [customCostSummary, setCustomCostSummary] = useState(null);
    const [savingCustomRates, setSavingCustomRates] = useState(false);
    const [fetchingRates, setFetchingRates] = useState(false);
    const [ratesAreDefault, setRatesAreDefault] = useState(true);
    // ---------- Material/Labour Cost state ----------
    const [materialRates, setMaterialRates] = useState([]);
    const [labourRates, setLabourRates] = useState([]);
    const [loadingMaterialRates, setLoadingMaterialRates] = useState(false);
    const [loadingLabourRates, setLoadingLabourRates] = useState(false);
    const [editableMaterialRates, setEditableMaterialRates] = useState({});
    const [editableLabourRates, setEditableLabourRates] = useState({});
    // Store raw input strings for material/labour rates so user can type decimals
    const [materialRateInputStrings, setMaterialRateInputStrings] = useState({});
    const [labourRateInputStrings, setLabourRateInputStrings] = useState({});
    // ---------- Track if rates have been saved (for download API choice) ----------
    const [materialRatesSaved, setMaterialRatesSaved] = useState(false);
    const [labourRatesSaved, setLabourRatesSaved] = useState(false);
    // ---------- Editable table toggle for pencil-icon ----------
    const [isCustomTableEditing, setIsCustomTableEditing] = useState(false);
    const [isMaterialTableEditing, setIsMaterialTableEditing] = useState(false);
    const [isLabourTableEditing, setIsLabourTableEditing] = useState(false);
    // ---------------------------------------
    // ---------- Upload Progress State ----------
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadProgressMessage, setUploadProgressMessage] = useState("");
    // Elapsed time + rotating tips while the AI estimation runs
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [tipIndex, setTipIndex] = useState(0);
    // -------------------------------------------
    // ---------- CPM State ----------
    const [cpmStartDate, setCpmStartDate] = useState("");
    const [cpmEndDate, setCpmEndDate] = useState("");
    const [cpmResult, setCpmResult] = useState(null);
    const [cpmLoading, setCpmLoading] = useState(false);
    const [cpmFilterStage, setCpmFilterStage] = useState("all");
    const [cpmActiveTab, setCpmActiveTab] = useState("tasks");
    const [expandedStageDiagram, setExpandedStageDiagram] = useState(null);
    // ---------------------------------------

    // Elapsed timer shown while the upload/estimation is running
    useEffect(() => {
        if (!saving) {
            setElapsedSeconds(0);
            setTipIndex(0);
            return;
        }
        const intervalId = setInterval(() => {
            setElapsedSeconds(prev => prev + 1);
        }, 1000);
        return () => clearInterval(intervalId);
    }, [saving]);

    // Rotate waiting tips while the AI stage (60% - 100%) is running
    useEffect(() => {
        if (!saving || uploadProgress < 60 || uploadProgress >= 100) return;
        const intervalId = setInterval(() => {
            setTipIndex(prev => (prev + 1) % estimationWaitingTips.length);
        }, 4000);
        return () => clearInterval(intervalId);
    }, [saving, uploadProgress]);

    // Auto-scroll to the error banner whenever a validation/API error is set
    // so the user always sees what needs to be fixed, even if they scrolled down.
    useEffect(() => {
        if (errorStatus) {
            requestAnimationFrame(() => {
                errorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        }
    }, [errorStatus]);

    // Clear the error banner as soon as the user corrects any of the
    // form fields that triggered the validation error (client, site,
    // subscription, project name, or drawing types).
    useEffect(() => {
        if (errorStatus) {
            setErrorStatus("");
        }
    }, [selectedSiteId, selectedClientId, selectedSubscriptionId, projectName, selectedDrawingTypeIds]); // eslint-disable-line react-hooks/exhaustive-deps

    // Restore persisted wizard state on initial mount (after clients load)
    // IN EDIT MODE: Skip localStorage restoration - backend data will be loaded instead
    useEffect(() => {
        if (initialRestoreDone.current) return;
        if (loadingClients) return; // Wait until clients are loaded so we can validate IDs

        // In edit mode, skip localStorage restoration - prioritize backend data
        if (isEditMode && projectIdFromUrl) {
            console.log(`Edit mode detected for project ${projectIdFromUrl}. Skipping localStorage restoration.`);
            initialRestoreDone.current = true;
            return;
        }

        // For new projects, restore from localStorage with projectId validation
        const saved = getPersisted(projectIdFromUrl);
        if (saved) {
            localStorageRestored.current = true;
            
            // Restore all persistable state fields
            if (saved.projectName) {
                setProjectName(saved.projectName);
                // If a project was already created (has projectId), restore the header name
                if (saved.projectId) {
                    dispatch(actions.setScreenName(saved.projectName));
                }
            }
            if (saved.selectedClientId) setSelectedClientId(saved.selectedClientId);
            if (saved.selectedSiteId) setSelectedSiteId(saved.selectedSiteId);
            if (saved.selectedSubscriptionId) setSelectedSubscriptionId(saved.selectedSubscriptionId);
            if (saved.floorsCount) setFloorsCount(saved.floorsCount);
            if (saved.selectedDrawingTypeIds) setSelectedDrawingTypeIds(saved.selectedDrawingTypeIds);
            if (saved.currentStep) setCurrentStep(saved.currentStep);
            if (saved.currentFloorIndex) setCurrentFloorIndex(saved.currentFloorIndex);
            if (saved.stepState) setStepState(saved.stepState);
            if (saved.projectId) setProjectId(saved.projectId);
            if (saved.costSummary) setCostSummary(saved.costSummary);
            if (saved.costTab) setCostTab(saved.costTab);
            if (saved.customRates) setCustomRates(saved.customRates);
            if (saved.rateInputStrings) setRateInputStrings(saved.rateInputStrings);
            if (saved.customCostSummary) setCustomCostSummary(saved.customCostSummary);
            if (saved.ratesAreDefault !== undefined) setRatesAreDefault(saved.ratesAreDefault);
            if (saved.cpmStartDate) setCpmStartDate(saved.cpmStartDate);
            if (saved.cpmEndDate) setCpmEndDate(saved.cpmEndDate);
            if (saved.cpmResult) setCpmResult(saved.cpmResult);

            // Restore moduleFiles — strip entries that lost their actual File reference after refresh
            if (saved.moduleFiles && typeof saved.moduleFiles === "object") {
                const cleanedFiles = {};
                let hasCleanFiles = false;
                for (const [stepKey, files] of Object.entries(saved.moduleFiles)) {
                    if (Array.isArray(files)) {
                        // After refresh, File objects are lost; keep only files that still have usable file references
                        // or that already have results computed (so we can show the results view)
                        const hasResultsForStep = saved.moduleResults && saved.moduleResults[stepKey];
                        const validFiles = files.filter(f => {
                            // Keep files that still have an actual File object (shouldn't happen after refresh but safe)
                            // OR keep them if results already exist (so user can see the results without re-uploading)
                            if (hasResultsForStep) return true;
                            // If no results, only keep files with actual file data
                            return f.file instanceof File || f.file !== undefined;
                        });
                        if (validFiles.length > 0) {
                            cleanedFiles[stepKey] = validFiles;
                            hasCleanFiles = true;
                        }
                    }
                }
                if (hasCleanFiles) {
                    setModuleFiles(cleanedFiles);
                }
            }
            // Restore moduleResults (serializable estimation data)
            if (saved.moduleResults && typeof saved.moduleResults === "object") {
                setModuleResults(saved.moduleResults);
            }
        }

        initialRestoreDone.current = true;
    }, [loadingClients, isEditMode, projectIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load project data when in edit mode
    useEffect(() => {
        if (!isEditMode || !projectIdFromUrl) return;
        if (!initialRestoreDone.current) return; // Wait for initial restore
        if (backendDataLoaded.current) return; // Already loaded
        
        fetchProjectForEdit(projectIdFromUrl);
    }, [isEditMode, projectIdFromUrl, initialRestoreDone.current]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-save wizard progress to backend
    useEffect(() => {
        if (!projectId || !backendDataLoaded.current) return;
        
        // Debounce save to avoid too many API calls
        const timeoutId = setTimeout(() => {
            saveWizardProgress();
        }, 2000); // Save 2 seconds after last state change
        
        return () => clearTimeout(timeoutId);
    }, [currentStep, currentFloorIndex, stepState, selectedDrawingTypeIds, costSummary, customRates, cpmResult]); // eslint-disable-line react-hooks/exhaustive-deps

    // Persist wizard state whenever critical state variables change
    useEffect(() => {
        // Don't persist until initial restore has finished
        if (!initialRestoreDone.current) return;

        setPersisted({
            projectName,
            selectedClientId,
            selectedSiteId,
            selectedSubscriptionId,
            floorsCount,
            selectedDrawingTypeIds,
            currentStep,
            currentFloorIndex,
            stepState,
            moduleFiles,
            moduleResults,
            projectId,
            costSummary,
            costTab,
            customRates,
            rateInputStrings,
            customCostSummary,
            ratesAreDefault,
            cpmStartDate,
            cpmEndDate,
            cpmResult
        });
    }, [
        projectName,
        selectedClientId,
        floorsCount,
        selectedDrawingTypeIds,
        currentStep,
        currentFloorIndex,
        stepState,
        moduleFiles,
        moduleResults,
        projectId,
        costSummary,
        costTab,
        customRates,
        rateInputStrings,
        customCostSummary,
        ratesAreDefault,
        cpmStartDate,
        cpmEndDate,
        cpmResult,
        setPersisted
    ]);

    // Clear localStorage when user navigates away from onboarding page
    // This ensures that partially entered data is not retained when user leaves
    // the onboarding flow without completing project creation
    useEffect(() => {
        const unlisten = history.listen((location, action) => {
            // Check if navigating away from onboarding pages
            const isLeavingOnboarding = 
                !location.pathname.startsWith('/projects/new') && 
                !location.pathname.startsWith('/projects/edit/');
            
            // Clear localStorage if leaving onboarding page
            // This handles cases where user clicks sidebar menu items (Sites, Clients, etc.)
            // before completing the project creation process
            if (isLeavingOnboarding) {
                console.log('Navigating away from onboarding page. Clearing localStorage.');
                clearPersisted();
            }
        });
        
        return () => unlisten();
    }, [history, clearPersisted]);

    // useEffect: recalculate customCostSummary whenever customRates changes
    useEffect(() => {
        if (costSummary && Object.keys(customRates).length > 0) {
            const updated = recalcCostWithCustomRates(costSummary, customRates);
            setCustomCostSummary(updated);
        }
    }, [customRates, costSummary]);

    // useEffect: Initialize custom rates when switching to custom tab
    useEffect(() => {
        if (costSummary && costTab === 'custom' && Object.keys(customRates).length === 0) {
            // Initialize with default rates from costSummary
            const defaultRates = {};
            const inputStrs = {};
            costSummary.rows.forEach(row => {
                if (row.type === 'item' && row.Item && row["Rate (₹)"] !== undefined) {
                    defaultRates[row.Item] = row["Rate (₹)"];
                    inputStrs[row.Item] = String(row["Rate (₹)"]);
                }
            });
            setCustomRates(defaultRates);
            setRateInputStrings(inputStrs);
        }
    }, [costSummary, costTab]);

    // useEffect: Sync rateInputStrings whenever customRates changes (including from API fetch)
    useEffect(() => {
        if (Object.keys(customRates).length > 0) {
            const inputStrs = {};
            Object.keys(customRates).forEach(key => {
                inputStrs[key] = String(customRates[key]);
            });
            setRateInputStrings(inputStrs);
        }
    }, [customRates]);

    // useEffect: Read project type from location state (passed from ProjectTypeSelector)
    useEffect(() => {
        if (props.location?.state?.projectType) {
            setProjectType(props.location.state.projectType);
        }
    }, [props.location]);

    const costSummaryMetrics = useMemo(() => {
        const source = costTab === 'custom' && customCostSummary ? customCostSummary : costSummary;
        const rows = source?.rows || [];
        const grandTotalRow = rows.find(row => row.type === "grand_total");
        const floorCount = rows.filter(row => row.type === "floor").length;
        const moduleCount = rows.filter(row => row.type === "module").length;
        const itemCount = rows.filter(row => !row.type).length;

        return {
            grandTotal: grandTotalRow?.["Amount (₹)"] || 0,
            floorCount,
            moduleCount,
            itemCount
        };
    }, [costSummary, customCostSummary, costTab]);

    useEffect(() => {
        // Clear selected client when site changes
        setSelectedClientId("");

        if (!selectedSiteId) {
            setClients([]);
            setLoadingClients(false);
            return;
        }

        setLoadingClients(true);
        // Fetch all clients and filter by the site_id field on the client document
        // The Client schema has a site_id field that references the site it belongs to
        getApi(clientsList).then(response => {
            const data = response?.data?.data || [];
            const allClients = data.map(client => ({
                id: client.id,
                name: client.name,
                contact: client.contact || client.phone || client.mobile || "No contact",
                site_id: client.site_id || ""
            }));

            // Filter to only show clients whose site_id matches the selected site
            const filteredClients = allClients.filter(client => String(client.site_id) === String(selectedSiteId));
            setClients(filteredClients);

            // If exactly one client is found, auto-select it
            if (filteredClients.length === 1) {
                setSelectedClientId(filteredClients[0].id);
            }
        }).catch(error => {
            console.log(error);
            setErrorStatus("Unable to load clients. Please try again.");
            setClients([]);
        }).finally(() => {
            setLoadingClients(false);
        });
    }, [selectedSiteId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        postApi(sitesList, { "per_page": 1000 }).then(response => {
            const data = response?.data?.data || [];
            setSites(data.map(site => ({
                id: site.id,
                name: site.name,
                location: site.location || "",
                description: site.description || "",
                client_id: site.client_id || ""
            })));
        }).catch(error => {
            console.log("Failed to load sites:", error);
        }).finally(() => {
            setLoadingSites(false);
        });

        getApi(plansAvailable).then(response => {
            const data = response?.data || [];
            const subs = Array.isArray(data) ? data : [];
            const mappedSubs = subs.map(sub => ({
                id: sub.subscription_id,
                planId: sub.plan_id,
                planName: sub.plan_name || "Plan",
                status: sub.available_count > 0 ? "active" : "inactive",
                availableCount: sub.available_count || 0
            }));
            setSubscriptions(mappedSubs);
        }).catch(error => {
            console.log("Failed to load subscriptions:", error);
            // Handle "No active plan" gracefully - set empty array
            setSubscriptions([]);
        }).finally(() => {
            setLoadingSubscriptions(false);
        });

        getApi(plansCurrent).then(response => {
            if (response?.data?.data && response.data.data.length > 0) {
                const activeSub = response.data.data[0];
                const planObj = activeSub.plan || {};
                const subObj = activeSub.subscription || {};
                const limitsObj = planObj.limits || {};

                setPlanName(planObj.name || "");
                setPlanLimits({
                    max_estimations: limitsObj.estimations !== undefined ? limitsObj.estimations : limitsObj.max_estimations,
                    max_floors: limitsObj.max_floor !== undefined ? limitsObj.max_floor : limitsObj.max_floors,
                    allowed_building_types: limitsObj.building_types || limitsObj.allowed_building_types || [],
                    allow_new_project: limitsObj.allow_new_project !== undefined ? limitsObj.allow_new_project : true
                });
                setSubscriptionUsage({
                    used_count: subObj.used_count || 0,
                    available_count: subObj.available_count || 0,
                    total_count: subObj.total_count || 0
                });
            }
        }).catch(error => {
            console.log("Failed to load current plan limits:", error);
        });

        getApi(projectsList).then(response => {
            const count = response?.data?.pagination?.total !== undefined 
                ? response.data.pagination.total 
                : (response?.data?.data?.length || 0);
            setProjectCount(count);
        }).catch(error => {
            console.log("Failed to load project count:", error);
        });
    }, []);

    const activeUploadSteps = useMemo(() => {
        return uploadSteps.filter(step => {
            if (currentFloorIndex > 1) {
                return selectedDrawingTypeIds.includes(step.id) && step.id !== "foundation" && step.id !== "plinth-beam";
            }
            return selectedDrawingTypeIds.includes(step.id);
        });
    }, [currentFloorIndex, selectedDrawingTypeIds]);

    const filteredSubscriptions = useMemo(() => {
        return subscriptions.filter(sub => !(sub.planName?.toUpperCase() === "FREE" && projectCount >= 1));
    }, [subscriptions, projectCount]);

    useEffect(() => {
        if (projectCount >= 1 && selectedSubscriptionId) {
            const selectedSub = subscriptions.find(sub => sub.id === selectedSubscriptionId);
            if (selectedSub && selectedSub.planName?.toUpperCase() === "FREE") {
                setSelectedSubscriptionId("");
            }
        }
    }, [projectCount, selectedSubscriptionId, subscriptions]);

    // Fetch plan details when subscription is selected
    useEffect(() => {
        if (!selectedSubscriptionId) {
            setSelectedSubscriptionPlan(null);
            setFloorsCount(1); // Reset to 1 when no subscription
            return;
        }

        // Fetch the plan details for the selected subscription
        setLoadingPlanDetails(true);
        const selectedSub = subscriptions.find(sub => sub.id === selectedSubscriptionId);
        
        if (selectedSub && selectedSub.planId) {
            // Fetch plan details using the plan_id
            getApi(`${planDetail}/${selectedSub.planId}`)
                .then(response => {
                    if (response?.data) {
                        setSelectedSubscriptionPlan(response.data);
                    }
                })
                .catch(error => {
                    console.error("Failed to fetch plan details:", error);
                    setSelectedSubscriptionPlan(null);
                })
                .finally(() => {
                    setLoadingPlanDetails(false);
                });
        } else {
            setLoadingPlanDetails(false);
        }
    }, [selectedSubscriptionId, subscriptions]);

    const maxFloorsAllowed = useMemo(() => {
        // Use selected subscription's plan limits if available, otherwise use current plan limits
        const limitsToUse = selectedSubscriptionPlan?.limits || planLimits;
        return getMaxFloorsAllowed(limitsToUse);
    }, [selectedSubscriptionPlan, planLimits]);

    const estimationLimitReached = useMemo(() => {
        // Only block estimation when the plan explicitly sets max_estimations to 0
        // (meaning the plan allows zero estimations by design).
        // We do NOT use subscriptionUsage.available_count here because that tracks
        // remaining project-creation slots, not estimation capacity.
        // The backend's validate_estimation_limit handles per-project estimation
        // limits (e.g. FREE plan allowing 1 estimation) and returns a proper
        // error response when exceeded.
        return planLimits && planLimits.max_estimations !== null && planLimits.max_estimations !== undefined && planLimits.max_estimations <= 0;
    }, [planLimits]);

    const projectLimitReached = useMemo(() => {
        // Check if user has exhausted their project creation quota
        // This is different from estimation limit - it's about creating NEW projects
        return subscriptionUsage.available_count <= 0 && filteredSubscriptions.length === 0;
    }, [subscriptionUsage, filteredSubscriptions]);

    useEffect(() => {
        if (floorsCount > maxFloorsAllowed) {
            setFloorsCount(maxFloorsAllowed);
        }
    }, [maxFloorsAllowed, floorsCount]);

    useEffect(() => {
        const allowed = getAllowedBuildingTypes(planLimits);
        if (allowed.length > 0 && !allowed.includes(buildingType)) {
            setBuildingType(allowed[0]);
        }
    }, [planLimits, buildingType]);

    const totalWizardSteps = activeUploadSteps.length;
    const hasTransitionStep = floorsCount > 1 && currentFloorIndex < floorsCount;
    const transitionStepNumber = totalWizardSteps + 2;
    const totalStepsInTitle = totalWizardSteps + 1 + (hasTransitionStep ? 1 : 0);
    const currentUploadStep = currentStep >= 2 && currentStep <= totalWizardSteps + 1 ? activeUploadSteps[currentStep - 2] : null;

    const getStepKey = (stepId) => `${currentFloorIndex}_${stepId}`;
    const uploadedFiles = currentUploadStep ? (moduleFiles[getStepKey(currentUploadStep.id)] || []) : [];
    const progress = Math.min(100, Math.round((currentStep / Math.max(totalStepsInTitle, 1)) * 100));
    const currentResult = currentUploadStep ? moduleResults[getStepKey(currentUploadStep.id)] : null;

    // Index of the active estimation stage derived from uploadProgress (-1 when idle)
    const activeStageIndex = useMemo(() => {
        if (!saving) return -1;
        let index = 0;
        estimationStages.forEach((stage, stageIndex) => {
            if (uploadProgress >= stage.min) index = stageIndex;
        });
        return index;
    }, [saving, uploadProgress]);

    const navigateBack = () => {
        clearPersisted();
        props.history.push("/projects");
    };

    // Fetch project data when in edit mode
    const fetchProjectForEdit = async (id) => {
        try {
            const response = await getApi(`${projectDetail}/${id}`);
            if (response.status === 200 && response.data) {
                const project = response.data;
                
                // Restore basic project metadata
                if (project.name) setProjectName(project.name);
                if (project.client_id) setSelectedClientId(project.client_id);
                if (project.site_id) setSelectedSiteId(project.site_id);
                if (project.subscription_id) setSelectedSubscriptionId(project.subscription_id);
                if (project.building_type) setBuildingType(project.building_type);
                setProjectId(id);
                dispatch(actions.setScreenName(project.name));
                
                // Restore wizard state from wizard_state field (preferred) or individual fields
                const ws = project.wizard_state;
                if (ws) {
                    // Restore from consolidated wizard_state object
                    if (ws.currentStep) setCurrentStep(ws.currentStep);
                    if (ws.currentFloorIndex) setCurrentFloorIndex(ws.currentFloorIndex);
                    if (ws.stepState) setStepState(ws.stepState);
                    if (ws.selectedDrawingTypeIds) setSelectedDrawingTypeIds(ws.selectedDrawingTypeIds);
                    if (ws.floorsCount) setFloorsCount(ws.floorsCount);
                    if (ws.costSummary) setCostSummary(ws.costSummary);
                    if (ws.customRates) setCustomRates(ws.customRates);
                    if (ws.cpmResult) setCpmResult(ws.cpmResult);
                } else {
                    // Fallback: restore from individual fields if wizard_state not present
                    if (project.current_step) setCurrentStep(project.current_step);
                    if (project.current_floor_index) setCurrentFloorIndex(project.current_floor_index);
                    if (project.step_state) setStepState(project.step_state);
                    if (project.selected_drawing_types) setSelectedDrawingTypeIds(project.selected_drawing_types);
                    if (project.floors_count) setFloorsCount(project.floors_count);
                }
                
                backendDataLoaded.current = true;
                
                // Clear localStorage after successfully loading backend data to prevent conflicts
                clearPersisted();
                console.log(`Backend data loaded for project ${id}. Cleared localStorage to prevent conflicts.`);
            }
        } catch (error) {
            console.error("Failed to load project for editing:", error);
            setErrorStatus("Failed to load project. Please try again.");
        }
    };

    // Save wizard progress to backend
    const saveWizardProgress = async () => {
        if (!projectId || !backendDataLoaded.current) return;
        
        try {
            const wizardState = {
                currentStep,
                currentFloorIndex,
                stepState,
                selectedDrawingTypeIds,
                floorsCount,
                costSummary,
                customRates,
                cpmResult
            };
            
            await putApi(`${projectDetail}/${projectId}`, {
                wizard_state: wizardState,
                current_step: currentStep,
                current_floor_index: currentFloorIndex,
                step_state: stepState,
                selected_drawing_types: selectedDrawingTypeIds,
                floors_count: floorsCount
            });
        } catch (error) {
            console.error("Failed to save wizard progress:", error);
            // Don't show error to user - this is background save
        }
    };

    const renderCardIcon = (id, active) => (
        <svg className="project-onboard-card-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
            <path strokeLinecap="round" strokeLinejoin="round" d={iconPath[id] || iconPath.foundation} />
        </svg>
    );

    const handleToggleCard = (cardId) => {
        if (!selectedSubscriptionId) {
            setErrorStatus("Please select a subscription plan first to choose drawing types.");
            return;
        }

        if (currentFloorIndex > 1 && (cardId === "foundation" || cardId === "plinth-beam")) return;

        const isLockedType = ["superstructure", "elevation", "lintel-beam", "staircase"].includes(cardId);
        const coreIds = currentFloorIndex > 1
            ? ["floor-beam", "floor-slab", "columns"]
            : ["foundation", "plinth-beam", "floor-beam", "floor-slab", "columns"];
        const isAllCoreSelected = coreIds.every(id => selectedDrawingTypeIds.includes(id));

        if (isLockedType && !isAllCoreSelected) return;

        // Lintel beam depends on superstructure (Plan) being selected first
        if (cardId === "lintel-beam" && !selectedDrawingTypeIds.includes("superstructure")) {
            setErrorStatus("Please select and upload the Plan (Superstructure) before selecting Lintel Beam.");
            return;
        }

        setSelectedDrawingTypeIds(prev => {
            // Check if trying to add new diagram
            if (!prev.includes(cardId)) {
                // Columns requires Floor Beam — auto-add it together with Columns
                const toAdd = cardId === "columns" && !prev.includes("floor-beam")
                    ? ["floor-beam", cardId]
                    : [cardId];

                // Use selected subscription's limits if available, otherwise use current plan limits
                const limitsToCheck = selectedSubscriptionPlan?.limits || planLimits;
                const maxEstimations = limitsToCheck?.estimations !== undefined && limitsToCheck?.estimations !== null
                    ? limitsToCheck.estimations
                    : limitsToCheck?.max_estimations !== undefined && limitsToCheck?.max_estimations !== null
                    ? limitsToCheck.max_estimations
                    : null;

                // If limit exists and would be exceeded, block and show message
                if (maxEstimations !== null && prev.length + toAdd.length > maxEstimations) {
                    const planToDisplay = selectedSubscriptionPlan?.name || planName || 'current';
                    setErrorStatus(
                        `Your ${planToDisplay} plan allows only ${maxEstimations} diagram${maxEstimations > 1 ? 's' : ''}. ` +
                        `Please upgrade your plan to upload more diagrams.`
                    );
                    return prev; // Don't add the new diagram(s)
                }

                // Clear error if within limit
                if (errorStatus && errorStatus.includes('diagram')) {
                    setErrorStatus('');
                }

                // Inform user when Floor Beam was auto-added for Columns
                if (toAdd.length > 1) {
                    setErrorStatus("Floor Beam added automatically — Columns requires Floor Beam sections.");
                }

                return [...prev, ...toAdd];
            }

            // Removing a diagram - clear any diagram limit error message
            if (errorStatus && errorStatus.includes('diagram')) {
                setErrorStatus('');
            }
            const nextSelection = prev.filter(id => id !== cardId);

            // If deselecting Floor Beam, automatically remove Columns (Columns requires Floor Beam)
            let cleaned = cardId === "floor-beam"
                ? nextSelection.filter(id => id !== "columns")
                : nextSelection;

            // If deselecting superstructure (Plan), automatically remove lintel-beam
            if (cardId === "superstructure" && cleaned.includes("lintel-beam")) {
                cleaned = cleaned.filter(id => id !== "lintel-beam");
            }

            const stillHasAllCore = coreIds.every(id => cleaned.includes(id));
            if (!stillHasAllCore) {
                return cleaned.filter(id => !["superstructure", "elevation", "lintel-beam", "staircase"].includes(id));
            }
            return cleaned;
        });
    };

    const handleStartUpload = async (event) => {
        event.preventDefault();
        if (!selectedClientId) {
            setErrorStatus("Please select a client for this project.");
            return;
        }
        if (!selectedSiteId) {
            setErrorStatus("Please select a site for this project.");
            return;
        }
        // Require subscription only if plans are available to select from
        if (!selectedSubscriptionId && filteredSubscriptions.length > 0) {
            setErrorStatus("Please select a subscription plan for this project.");
            return;
        }
        if (!projectName.trim()) {
            setErrorStatus("Please provide a project name.");
            return;
        }
        if (!selectedDrawingTypeIds.length) {
            setErrorStatus("Please select at least one drawing type to upload.");
            return;
        }

        // Validate plan restrictions before project creation
        // Use selected subscription's plan limits if available, otherwise use current plan limits
        const limitsToValidate = selectedSubscriptionPlan?.limits || planLimits;
        const validation = validateProjectCreation({
            floorsCount: floorsCount,
            buildingType: buildingType,
            planLimits: limitsToValidate,
            subscription: subscriptionUsage,
            existingProjectsCount: projectCount
        });

        if (!validation.valid) {
            setErrorStatus(validation.errors.join(' '));
            return;
        }

        setSaving(true);
        setErrorStatus("");

        try {
            let activeProjectId = projectId;

            if (!activeProjectId) {
                const payload = {
                    client_id: selectedClientId,
                    site_id: selectedSiteId,
                    name: projectName.trim(),
                    building_type: buildingType,
                    project_type: projectType || "RESIDENTIAL" // Default to RESIDENTIAL if not set
                };
                // Only include subscription_id if a valid subscription is selected (e.g., Free plan uses empty string)
                if (selectedSubscriptionId) {
                    payload.subscription_id = selectedSubscriptionId;
                }
                const response = await postApi(projectAdd, payload);

                if (response.status !== 200 && response.status !== 201) {
                    setErrorStatus("Project could not be created. Please try again.");
                    return;
                }

                activeProjectId = response.data?.id;
                setProjectId(activeProjectId);
                dispatch(actions.setScreenName(projectName.trim()));
                
                // Mark backend data as loaded since we just created the project
                backendDataLoaded.current = true;
            }

            if (!activeProjectId) {
                setErrorStatus("Project could not be created. Missing project id.");
                return;
            }

            setCurrentStep(2);
            setStepState("upload");
            
            // Immediately save wizard state to backend after project creation
            // This ensures state is persisted even if user closes browser immediately
            if (activeProjectId) {
                try {
                    const wizardState = {
                        currentStep: 2,
                        currentFloorIndex,
                        stepState: "upload",
                        selectedDrawingTypeIds,
                        floorsCount,
                        costSummary: null,
                        customRates: {},
                        cpmResult: null
                    };
                    
                    await putApi(`${projectDetail}/${activeProjectId}`, {
                        wizard_state: wizardState,
                        current_step: 2,
                        current_floor_index: currentFloorIndex,
                        step_state: "upload",
                        selected_drawing_types: selectedDrawingTypeIds,
                        floors_count: floorsCount
                    });
                    console.log(`Wizard state saved immediately after project creation for project ${activeProjectId}`);
                } catch (saveError) {
                    console.error("Failed to save initial wizard state:", saveError);
                    // Don't block user flow on save error
                }
            }
        } catch (error) {
            console.log(error);
            setErrorStatus(safeErrorMessage(error, "Project could not be created. Please try again."));
        } finally {
            setSaving(false);
        }
    };

    const addFiles = (files) => {
        if (saving) return;
        const validFiles = files.filter(file => file.type.startsWith("image/") || file.type === "application/pdf");
        if (!validFiles.length) {
            setErrorStatus("Please select valid image or PDF files.");
            return;
        }

        const mappedFiles = validFiles.map(file => ({
            id: `file-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            url: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
            name: file.name,
            type: file.type,
            size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
            file
        }));

        if (currentUploadStep) {
            const stepKey = getStepKey(currentUploadStep.id);
            setModuleFiles(prev => ({
                ...prev,
                [stepKey]: [...(prev[stepKey] || []), ...mappedFiles]
            }));
        }
        setErrorStatus("");
    };

    const removeFile = (fileId) => {
        if (!currentUploadStep) return;

        const stepKey = getStepKey(currentUploadStep.id);
        setModuleFiles(prev => ({
            ...prev,
            [stepKey]: (prev[stepKey] || []).filter(file => file.id !== fileId)
        }));
    };

    const handleNextStep = async (event) => {
        if (event) event.preventDefault();
        if (!currentUploadStep) return;

        if (!uploadedFiles.length) {
            setErrorStatus(`Please upload at least one drawing for ${currentUploadStep.title}.`);
            return;
        }

        if (currentResult) {
            setErrorStatus("");
            setStepState("results");
            return;
        }

        await uploadAndEstimateCurrentStep();
    };

    const uploadAndEstimateCurrentStep = async () => {
        const apiConfig = moduleApiConfig[currentUploadStep.id];

        if (!apiConfig) {
            setErrorStatus(`No estimation API is configured for ${currentUploadStep.title}.`);
            return;
        }

        if (!projectId) {
            setErrorStatus("Project is not ready yet. Please go back and create the project again.");
            return;
        }

        setSaving(true);
        setErrorStatus("");
        setUploadProgress(0);
        setUploadProgressMessage("Starting upload process...");

        // Helper to advance progress smoothly
        const advanceProgress = (target, message) => {
            setUploadProgress(target);
            setUploadProgressMessage(message);
        };

        try {
            advanceProgress(10, "Preparing files for upload...");

            await new Promise(r => setTimeout(r, 100)); // Small tick for UI update

            advanceProgress(20, "Converting images to base64 format...");
            const images = await Promise.all(uploadedFiles.map(file => fileToBase64(file.file)));

            advanceProgress(40, "Uploading drawings to server...");
            const floorLabel = floorNames[currentFloorIndex - 1] || `Floor ${currentFloorIndex}`;

            const estimationResponse = await postApi(projectEstimation, {
                client_id: selectedClientId,
                site_id: selectedSiteId,
                name: projectName.trim(),
                project_id: projectId,
                images,
                module: apiConfig.module,
                status: "pending",
                num_floors: floorsCount
            });

            const estimationId = estimationResponse.data?.id;

            if (!estimationId) {
                advanceProgress(0, "");
                setErrorStatus("Drawing upload completed, but the backend did not return an estimation id.");
                return;
            }

            advanceProgress(70, "Running AI estimation...");

            const estimatePayload = {
                estimation_id: estimationId,
                project_id: projectId,
                image_urls: estimationResponse.data?.images || []
            };

            if (apiConfig.floorAware) {
                estimatePayload.floor_label = floorLabel;
            }

            const estimateResponse = await postApi(apiConfig.api, estimatePayload);
            const responseData = estimateResponse.data || {};

            advanceProgress(90, "Processing estimation results...");

            const resultSource = responseData.summary || responseData.metrics || responseData.rows || responseData;

            const newModuleResults = {
                ...moduleResults,
                [getStepKey(currentUploadStep.id)]: {
                    estimationId,
                    floorLabel,
                    imageUrls: estimationResponse.data?.images || [],
                    rows: flattenResultRows(resultSource),
                    raw: responseData,
                    excelUrl: responseData.excel_url || null
                }
            };
            
            setModuleResults(newModuleResults);

            advanceProgress(100, "Complete!");
            
            // Immediately save wizard state after estimation completes
            // This ensures state is persisted even if user closes browser immediately
            if (projectId && backendDataLoaded.current) {
                try {
                    const wizardState = {
                        currentStep,
                        currentFloorIndex,
                        stepState: "results",
                        selectedDrawingTypeIds,
                        floorsCount,
                        costSummary,
                        customRates,
                        cpmResult
                    };
                    
                    await putApi(`${projectDetail}/${projectId}`, {
                        wizard_state: wizardState,
                        current_step: currentStep,
                        current_floor_index: currentFloorIndex,
                        step_state: "results",
                        selected_drawing_types: selectedDrawingTypeIds,
                        floors_count: floorsCount
                    });
                    console.log(`Wizard state saved immediately after estimation for project ${projectId}`);
                } catch (saveError) {
                    console.error("Failed to save wizard state after estimation:", saveError);
                    // Don't block user flow on save error
                }
            }
            
            await new Promise(r => setTimeout(r, 400)); // Let user see 100%
            setUploadProgress(0);
            setUploadProgressMessage("");
            setStepState("results");
        } catch (error) {
            console.log(error);
            setUploadProgress(0);
            setUploadProgressMessage("");
            setErrorStatus(safeErrorMessage(error, `Unable to calculate ${currentUploadStep.title} estimation.`));
        } finally {
            setSaving(false);
        }
    };

    const handlePrevStep = () => {
        setErrorStatus("");
        if (stepState === "results") {
            setStepState("upload");
            return;
        }

        if (currentStep > 2) {
            setCurrentStep(prev => prev - 1);
            setStepState("results");
        } else {
            setCurrentStep(1);
        }
    };

    const downloadCsv = async (rows, moduleName) => {
        const csvContent = "Item,Quantity,Unit\n"
            + rows.map(row => `"${row.item}",${row.quantity},"${row.unit}"`).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
        const fileName = `${projectName.trim().replace(/\s+/g, "_") || "Project"}_${moduleName}_Takeoff.csv`;
        await saveFileWithPrompt(blob, fileName);
    };

    /**
     * Download excel from backend blob URL and trigger browser download.
     */
    const downloadExcelFromUrl = async (excelUrl, fileName) => {
        try {
            const response = await fetch(excelUrl);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            await saveFileWithPrompt(blob, fileName);
        } catch (error) {
            console.error("Failed to download Excel from URL:", error);
            setErrorStatus("Failed to download Excel file from server. Falling back to client-side generation.");
            // Fallback to client-side generation
            await generateClientSideExcel();
        }
    };

    /**
     * Client-side Excel generation (original logic preserved).
     */
    const generateClientSideExcel = async () => {
        const source = costTab === 'custom' && customCostSummary ? customCostSummary : costSummary;
        if (!source || !source.rows) return;
        
        const wb = XLSX.utils.book_new();
        const excelData = [];
        
        excelData.push(['Floor / Module', 'Item', 'Unit', 'Quantity', 'Rate (₹)', 'Amount (₹)']);
        
        source.rows.forEach(row => {
            excelData.push([
                row['Floor / Module'] || '',
                row.Item || '',
                row.Unit || '',
                row.Quantity !== undefined && row.Quantity !== null ? row.Quantity : '',
                row['Rate (₹)'] !== undefined && row['Rate (₹)'] !== null ? row['Rate (₹)'] : '',
                row['Amount (₹)'] !== undefined && row['Amount (₹)'] !== null ? row['Amount (₹)'] : ''
            ]);
        });
        
        const ws = XLSX.utils.aoa_to_sheet(excelData);
        
        ws['!cols'] = [
            { wch: 20 },
            { wch: 40 },
            { wch: 10 },
            { wch: 12 },
            { wch: 15 },
            { wch: 15 }
        ];
        
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                if (!ws[cellAddress]) continue;
                if (!ws[cellAddress].s) ws[cellAddress].s = {};
                if (R === 0) {
                    ws[cellAddress].s = {
                        font: { bold: true, color: { rgb: "FFFFFF" } },
                        fill: { fgColor: { rgb: "4C3E80" } },
                        alignment: { horizontal: "center", vertical: "center" }
                    };
                } else {
                    const row = source.rows[R - 1];
                    if (row) {
                        const isFloorBanner = row.type === 'floor';
                        const isModuleBanner = row.type === 'module';
                        const isSubtotal = row.type === 'module_subtotal' || row.type === 'floor_total';
                        const isGrandTotal = row.type === 'grand_total';
                        if (isFloorBanner) {
                            ws[cellAddress].s = {
                                font: { bold: true, color: { rgb: "FFFFFF" } },
                                fill: { fgColor: { rgb: "7C3AED" } },
                                alignment: { horizontal: "left", vertical: "center" }
                            };
                        } else if (isModuleBanner) {
                            ws[cellAddress].s = {
                                font: { bold: true, color: { rgb: "475569" } },
                                fill: { fgColor: { rgb: "F1F5F9" } },
                                alignment: { horizontal: "left", vertical: "center" }
                            };
                        } else if (isSubtotal) {
                            ws[cellAddress].s = {
                                font: { bold: true, color: { rgb: "92400E" } },
                                fill: { fgColor: { rgb: "FEF3C7" } },
                                alignment: { horizontal: C >= 3 ? "right" : "left", vertical: "center" }
                            };
                        } else if (isGrandTotal) {
                            ws[cellAddress].s = {
                                font: { bold: true, color: { rgb: "FFFFFF" } },
                                fill: { fgColor: { rgb: "00CFA5" } },
                                alignment: { horizontal: C >= 3 ? "right" : "left", vertical: "center" }
                            };
                        } else {
                            ws[cellAddress].s = {
                                alignment: { horizontal: C >= 3 ? "right" : "left", vertical: "center" }
                            };
                        }
                    }
                }
            }
        }
        
        XLSX.utils.book_append_sheet(wb, ws, 'Cost Breakdown');
        const suffix = costTab === 'custom' ? '_Custom_Cost' : '_Cost_Summary';
        const fileName = `${projectName.trim().replace(/\s+/g, "_") || "Project"}${suffix}.xlsx`;
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        await saveFileWithPrompt(blob, fileName);
    };

    /**
     * Hybrid download:
     * - If user has saved custom rates OR saved material/labour rates, call the project-rates API (run-cost-summary)
     * - Otherwise call the default-rates API (run-rates-summary)
     * - If API returns an excel_url, download it; otherwise fall back to client-side generation.
     */
    const downloadCostSummary = async () => {
        const source = costTab === 'custom' && customCostSummary ? customCostSummary : costSummary;
        if (!source || !source.rows) return;

        // Determine if any saved overrides exist
        const hasCustomRates = !ratesAreDefault;
        const hasMaterialRates = materialRatesSaved;
        const hasLabourRates = labourRatesSaved;
        const hasAnyOverrides = hasCustomRates || hasMaterialRates || hasLabourRates;

        // Check if currently viewing material or labour tab (those don't call APIs)
        if (costTab === 'material' || costTab === 'labour') {
            await generateClientSideExcel();
            return;
        }

        // Determine which API to call
        const apiUrl = hasAnyOverrides ? estimationRunCostSummary : estimationRunRatesSummary;
        const apiName = hasAnyOverrides ? 'run-cost-summary' : 'run-rates-summary';

        if (!projectId) {
            // No project — fallback to client-side
            await generateClientSideExcel();
            return;
        }

        try {
            // Let backend auto-discover floors from actual data in MongoDB
            const response = await postApi(apiUrl, { 
                project_id: projectId
                // num_floors removed - backend will discover from data
            });

            if (response.status === 200 && response.data) {
                const excelUrl = response.data.excel_url;
                if (excelUrl) {
                    const suffix = hasAnyOverrides ? '_Project_Cost' : '_Default_Cost';
                    const fileName = `${projectName.trim().replace(/\s+/g, "_") || "Project"}${suffix}.xlsx`;
                    await downloadExcelFromUrl(excelUrl, fileName);
                    return;
                }
            }
            // If no excel_url, fallback to client-side
            await generateClientSideExcel();
        } catch (error) {
            console.error(`Failed to call ${apiName}:`, error);
            setErrorStatus(`Failed to generate Excel via server. Falling back to client-side generation.`);
            await generateClientSideExcel();
        }
    };

    const downloadResult = async (result, rows, moduleName) => {
        if (result?.excelUrl) {
            const fileName = `${projectName.trim().replace(/\s+/g, "_") || "Project"}_${moduleName}.xlsx`;
            await downloadUrlWithPrompt(result.excelUrl, fileName);
            return;
        }

        await downloadCsv(rows, moduleName);
    };

    const downloadAllDiagrams = async () => {
        if (!cpmResult) return;

        const zip = new JSZip();
        const fetchPromises = [];

        // Add mother diagram
        if (cpmResult.mother_diagram) {
            const promise = fetch(cpmResult.mother_diagram)
                .then(res => {
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    return res.blob();
                })
                .then(blob => {
                    zip.file("cpm_mother_diagram.png", blob);
                })
                .catch(err => console.error("Failed to download mother diagram:", err));
            fetchPromises.push(promise);
        }

        // Add stage diagrams
        if (cpmResult.stage_diagrams) {
            Object.entries(cpmResult.stage_diagrams).forEach(([label, url]) => {
                const safeName = `cpm_${label.replace(/\s+/g, "_")}.png`;
                const promise = fetch(url)
                    .then(res => {
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        return res.blob();
                    })
                    .then(blob => {
                        zip.file(safeName, blob);
                    })
                    .catch(err => console.error(`Failed to download stage diagram "${label}":`, err));
                fetchPromises.push(promise);
            });
        }

        try {
            await Promise.all(fetchPromises);
            const zipBlob = await zip.generateAsync({ type: "blob" });
            saveAs(zipBlob, `CPM_Diagrams_${projectName.trim().replace(/\s+/g, "_") || "Project"}.zip`);
        } catch (err) {
            console.error("Failed to generate ZIP:", err);
        }
    };

    const calculateProjectCost = async () => {
        setCalculatingCost(true);
        setErrorStatus("");

        try {
            if (projectId) {
                // Let backend auto-discover floors from actual data in MongoDB
                // This ensures resumed projects show all uploaded floors correctly
                const response = await postApi(estimationRunRatesSummaryJson, { 
                    project_id: projectId
                    // num_floors removed - backend will discover from data
                });
                
                if (response.status === 200 && response.data) {
                    setCostSummary(response.data);
                    setStepState("cost-summary");
                    // Also try to fetch custom rates for this project
                    fetchProjectRates();
                }
            }
        } catch (error) {
            console.log(error);
            setErrorStatus(safeErrorMessage(error, "Unable to calculate project cost. Please try again."));
        } finally {
            setCalculatingCost(false);
        }
    };

    // ---------- Material/Labour Cost Functions ----------
    const fetchMaterialRates = async () => {
        setLoadingMaterialRates(true);
        try {
            const response = await getApi(materialRatesGet);
            if (response.status === 200 && response.data) {
                const ratesData = response.data.rates || {};
                const ratesArray = Object.entries(ratesData).map(([label, value]) => ({
                    label,
                    rate: typeof value === 'object' ? value.rate : value,
                    unit: typeof value === 'object' ? value.unit : '-',
                }));
                setMaterialRates(ratesArray);
            }
        } catch (error) {
            console.error("Failed to fetch material rates:", error);
        } finally {
            setLoadingMaterialRates(false);
        }
    };

    const fetchLabourRates = async () => {
        setLoadingLabourRates(true);
        try {
            const response = await getApi(labourRatesGet);
            if (response.status === 200 && response.data) {
                const ratesData = response.data.rates || {};
                const ratesArray = Object.entries(ratesData).map(([label, value]) => ({
                    label,
                    rate: typeof value === 'object' ? value.rate : value,
                    unit: typeof value === 'object' ? value.unit : '-',
                }));
                setLabourRates(ratesArray);
            }
        } catch (error) {
            console.error("Failed to fetch labour rates:", error);
        } finally {
            setLoadingLabourRates(false);
        }
    };

    const handleShowMaterialCost = () => {
        setCostTab('material');
        if (materialRates.length === 0) {
            fetchMaterialRates();
        }
    };

    const handleShowLabourCost = () => {
        setCostTab('labour');
        if (labourRates.length === 0) {
            fetchLabourRates();
        }
    };

    const handleMaterialRateChange = (label, rawValue) => {
        setMaterialRateInputStrings(prev => ({
            ...prev,
            [label]: rawValue
        }));
        const numericRate = rawValue === '' ? '' : parseFloat(rawValue);
        if (rawValue !== '' && isNaN(numericRate)) return;
        setEditableMaterialRates(prev => ({
            ...prev,
            [label]: rawValue === '' ? '' : numericRate
        }));
        // Also update the underlying materialRates array for display
        setMaterialRates(prev => prev.map(item =>
            item.label === label
                ? { ...item, rate: rawValue === '' ? '' : numericRate }
                : item
        ));
    };

    const handleLabourRateChange = (label, rawValue) => {
        setLabourRateInputStrings(prev => ({
            ...prev,
            [label]: rawValue
        }));
        const numericRate = rawValue === '' ? '' : parseFloat(rawValue);
        if (rawValue !== '' && isNaN(numericRate)) return;
        setEditableLabourRates(prev => ({
            ...prev,
            [label]: rawValue === '' ? '' : numericRate
        }));
        // Also update the underlying labourRates array for display
        setLabourRates(prev => prev.map(item =>
            item.label === label
                ? { ...item, rate: rawValue === '' ? '' : numericRate }
                : item
        ));
    };

    // ---------- Material/Labour Save Functions ----------
    const handleSaveMaterialRates = async () => {
        if (!projectId) return;
        setSavingCustomRates(true);
        setErrorStatus("");
        try {
            const response = await putApi(`${materialRatesSave}/${projectId}`, { rates: editableMaterialRates });
            if (response.status === 200) {
                setMaterialRatesSaved(true);
                // Store recomputed composite rates so Fixed Cost tab uses them
                const recomputedComposite = response.data?.recomputed_composite;
                if (recomputedComposite && typeof recomputedComposite === 'object') {
                    const extractedRates = {};
                    Object.keys(recomputedComposite).forEach(key => {
                        const val = recomputedComposite[key];
                        extractedRates[key] = typeof val === 'object' && val.rate !== undefined ? val.rate : val;
                    });
                    setCustomRates(extractedRates);
                    // Update input strings for the editable UI
                    const inputStrs = {};
                    Object.keys(extractedRates).forEach(key => {
                        inputStrs[key] = String(extractedRates[key]);
                    });
                    setRateInputStrings(inputStrs);
                }
                setSaveSuccessMessage("Material rates saved successfully!");
                setTimeout(() => setSaveSuccessMessage(null), 3000);
            }
        } catch (error) {
            console.log(error);
            setErrorStatus(safeErrorMessage(error, "Failed to save material rates."));
        } finally {
            setSavingCustomRates(false);
        }
    };

    const handleSaveLabourRates = async () => {
        if (!projectId) return;
        setSavingCustomRates(true);
        setErrorStatus("");
        try {
            const response = await putApi(`${labourRatesSave}/${projectId}`, { rates: editableLabourRates });
            if (response.status === 200) {
                setLabourRatesSaved(true);
                setSaveSuccessMessage("Labour rates saved successfully!");
                setTimeout(() => setSaveSuccessMessage(null), 3000);
            }
        } catch (error) {
            console.log(error);
            setErrorStatus(safeErrorMessage(error, "Failed to save labour rates."));
        } finally {
            setSavingCustomRates(false);
        }
    };

    const handleResetMaterialRates = async () => {
        if (!projectId) return;
        setSavingCustomRates(true);
        setErrorStatus("");
        try {
            const response = await postApi(`${materialRatesSave}/${projectId}/reset`, {});
            if (response.status === 200) {
                setMaterialRatesSaved(false);
                await fetchMaterialRates();
                setSaveSuccessMessage("Material rates reset to default!");
                setTimeout(() => setSaveSuccessMessage(null), 3000);
            }
        } catch (error) {
            console.log(error);
            setErrorStatus(safeErrorMessage(error, "Failed to reset material rates."));
        } finally {
            setSavingCustomRates(false);
        }
    };

    const handleResetLabourRates = async () => {
        if (!projectId) return;
        setSavingCustomRates(true);
        setErrorStatus("");
        try {
            const response = await postApi(`${labourRatesSave}/${projectId}/reset`, {});
            if (response.status === 200) {
                setLabourRatesSaved(false);
                await fetchLabourRates();
                setSaveSuccessMessage("Labour rates reset to default!");
                setTimeout(() => setSaveSuccessMessage(null), 3000);
            }
        } catch (error) {
            console.log(error);
            setErrorStatus(safeErrorMessage(error, "Failed to reset labour rates."));
        } finally {
            setSavingCustomRates(false);
        }
    };
    // ---------------------------------------

    // ---------- Edit mode toggle functions ----------
    const toggleCustomTableEdit = () => setIsCustomTableEditing(prev => !prev);
    const toggleMaterialTableEdit = () => setIsMaterialTableEditing(prev => !prev);
    const toggleLabourTableEdit = () => setIsLabourTableEditing(prev => !prev);
    // ---------------------------------------

    // ---------- Custom Cost Functions ----------
    const fetchProjectRates = async () => {
        if (!projectId) return;
        setFetchingRates(true);
        try {
            const response = await getApi(`${projectRatesGet}/${projectId}`);
            if (response.status === 200 && response.data) {
                const ratesData = response.data.rates || {};
                // Backend returns rates as objects with {rate, unit}, extract just the rate values
                const extractedRates = {};
                Object.keys(ratesData).forEach(key => {
                    const rateObj = ratesData[key];
                    // Handle both object format {rate: X, unit: Y} and plain number format
                    extractedRates[key] = typeof rateObj === 'object' && rateObj.rate !== undefined 
                        ? rateObj.rate 
                        : rateObj;
                });
                setCustomRates(extractedRates);
                setRatesAreDefault(response.data.is_default !== false);
            }
        } catch (error) {
            console.log("Could not fetch project rates:", error);
            // Silently fail - custom rates will use defaults
        } finally {
            setFetchingRates(false);
        }
    };

    const handleTabSwitch = async (tab) => {
        setCostTab(tab);
        // Option A: When switching to Fixed Cost tab, call server for project-specific cost summary
        if (tab !== 'custom' || !projectId) {
            // Don't clear customCostSummary - keep it in memory so switching tabs preserves custom calculations
            return;
        }
        setCalculatingCost(true);
        try {
            // Let backend auto-discover floors from actual data in MongoDB
            const response = await postApi(estimationRunCostSummaryJson, { 
                project_id: projectId
                // num_floors removed - backend will discover from data
            });
            if (response.status === 200 && response.data) {
                // Replace costSummary with server-computed data (Option A – no client-side recalc)
                setCostSummary(response.data);
                setCustomCostSummary(null);
                // Update ratesAreDefault from response meta
                const ratesSource = response.data?.meta?.rates_source;
                setRatesAreDefault(ratesSource !== 'project');
                // Initialize customRates from the server response rows
                const newRates = {};
                const newInputStrs = {};
                if (response.data.rows) {
                    response.data.rows.forEach(row => {
                        if (row.type === 'item' && row.Item && row["Rate (₹)"] !== undefined) {
                            newRates[row.Item] = row["Rate (₹)"];
                            newInputStrs[row.Item] = String(row["Rate (₹)"]);
                        }
                    });
                }
                if (Object.keys(newRates).length > 0) {
                    setCustomRates(newRates);
                    setRateInputStrings(newInputStrs);
                }
            }
        } catch (error) {
            console.error("Failed to fetch server cost summary:", error);
            // Silently fall back to existing costSummary in state
        } finally {
            setCalculatingCost(false);
        }
    };

    const handleRateChange = (itemName, rawValue) => {
        // Store the raw string input so user can type decimals properly
        setRateInputStrings(prev => ({
            ...prev,
            [itemName]: rawValue
        }));

        // Parse numeric value (allow empty string to clear)
        const numericRate = rawValue === '' ? 0 : parseFloat(rawValue);
        if (rawValue !== '' && isNaN(numericRate)) return; // Don't update if invalid

        // Update customRates; the useEffect below will recalculate customCostSummary automatically
        setCustomRates(prevRates => ({
            ...prevRates,
            [itemName]: isNaN(numericRate) ? 0 : numericRate
        }));
    };

    const handleSaveCustomRates = async () => {
        if (!projectId) return;
        setSavingCustomRates(true);
        setErrorStatus("");
        try {
            const response = await putApi(`${projectRatesSave}/${projectId}`, { rates: customRates });
            if (response.status === 200) {
                setRatesAreDefault(false);
                setSaveSuccessMessage("Custom rates saved successfully!");
                setTimeout(() => setSaveSuccessMessage(null), 3000);
            }
        } catch (error) {
            console.log(error);
            setErrorStatus(safeErrorMessage(error, "Failed to save custom rates."));
        } finally {
            setSavingCustomRates(false);
        }
    };

    const handleResetCustomRates = async () => {
        if (!projectId) return;
        setSavingCustomRates(true);
        setErrorStatus("");
        try {
            const response = await postApi(`${projectRatesReset}/${projectId}/reset`, {});
            if (response.status === 200) {
                // Re-fetch rates to get defaults
                await fetchProjectRates();
                setRatesAreDefault(true);
                setSaveSuccessMessage("Rates reset to default!");
                setTimeout(() => setSaveSuccessMessage(null), 3000);
            }
        } catch (error) {
            console.log(error);
            setErrorStatus(safeErrorMessage(error, "Failed to reset rates."));
        } finally {
            setSavingCustomRates(false);
        }
    };

    // ---------- CPM Handlers ----------
    const handleRunCpm = () => {
        setStepState("cpm-date-input");
    };

    const handleBackFromDateInput = () => {
        setStepState("cost-summary");
    };

    const handleGenerateCpm = async () => {
        if (!cpmStartDate) {
            setErrorStatus("Please provide a start date.");
            return;
        }
        setCpmLoading(true);
        setErrorStatus("");
        try {
            const payload = {
                project_id: projectId,
                start_date: cpmStartDate
            };
            if (cpmEndDate) {
                payload.target_end_date = cpmEndDate;
            }
            const response = await postApi(runCpm, payload);
            if (response.status === 200 && response.data) {
                setCpmResult(response.data);
                setStepState("cpm-result");
            } else {
                setErrorStatus("Failed to generate CPM schedule. Please try again.");
            }
        } catch (error) {
            console.log(error);
            setErrorStatus(safeErrorMessage(error, "Unable to generate CPM schedule."));
        } finally {
            setCpmLoading(false);
        }
    };
    // -----------------------------------

    const finishProject = async () => {
        if (!projectId) {
            clearPersisted();
            props.history.push("/projects");
            return;
        }
        try {
            setSaving(true);
            await putApi(`${projectDetail}/${projectId}`, { status: 'completed' });
        } catch (error) {
            console.error("Failed to mark project as completed:", error);
        } finally {
            setSaving(false);
        }
        clearPersisted();
        props.history.push("/projects");
    };

    // ---------- CPM Helper Functions ----------
    const getUniqueStages = () => {
        if (!cpmResult?.all_activities) return [];
        const stages = [...new Set(cpmResult.all_activities.map(act => act.stage_label || act.phase))];
        return stages.filter(Boolean);
    };

    const getFilteredActivities = () => {
        if (!cpmResult?.all_activities) return [];
        if (cpmFilterStage === "all") return cpmResult.all_activities;
        return cpmResult.all_activities.filter(act => 
            (act.stage_label === cpmFilterStage || act.phase === cpmFilterStage)
        );
    };

    const getCriticalCount = () => {
        if (!cpmResult?.all_activities) return 0;
        return cpmResult.all_activities.filter(act => act.critical).length;
    };

    const getTaskStatusCounts = () => {
        if (!cpmResult?.all_activities) {
            return { total: 0, completed: 0, inProgress: 0, critical: 0, notStarted: 0 };
        }
        const today = moment().startOf('day');
        let completed = 0;
        let inProgress = 0;
        let notStarted = 0;
        let critical = 0;

        cpmResult.all_activities.forEach(act => {
            const es = moment(act.es);
            const ef = moment(act.ef);

            if (today.isAfter(ef)) {
                completed++;
            } else if (today.isSameOrAfter(es) && today.isSameOrBefore(ef)) {
                inProgress++;
            } else {
                notStarted++;
            }

            if (act.critical) {
                critical++;
            }
        });

        return {
            total: cpmResult.all_activities.length,
            completed,
            inProgress,
            notStarted,
            critical,
        };
    };

    const getUniquePhases = () => {
        if (!cpmResult?.stages) return [];
        const phases = [...new Set(cpmResult.stages.map(s => s.phase))];
        return phases.filter(Boolean);
    };

    const getPhaseColor = (phase) => {
        const phaseColors = {
            'Substructure': '#7c3aed',
            'Superstructure': '#2563eb',
            'Masonry': '#d97706',
            'Services': '#059669',
            'Finishes': '#dc2626',
            'Fittings': '#7c3aed',
            'Handover': '#059669',
        };
        return phaseColors[phase] || '#6b7280';
    };

    // ---------- CPM Tab Rendering Functions ----------
    const renderTasksTab = () => {
        const filteredActivities = getFilteredActivities();
        const stages = getUniqueStages();
        
        return (
            <>
                {/* Stage Filter */}
                {stages.length > 0 && (
                    <div className="cpm-stage-filter mb-4">
                        <span className="filter-label">Filter by Stage:</span>
                        <div className="filter-badges">
                            <span
                                className={`filter-badge ${cpmFilterStage === "all" ? "active" : ""}`}
                                onClick={() => setCpmFilterStage("all")}
                            >
                                All Stages
                            </span>
                            {stages.map((stage, idx) => (
                                <span
                                    key={idx}
                                    className={`filter-badge ${cpmFilterStage === stage ? "active" : ""}`}
                                    onClick={() => setCpmFilterStage(stage)}
                                >
                                    {stage}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Activities Table */}
                <div className="cpm-activities-section">
                    <h3>All Activities</h3>
                    {filteredActivities.length > 0 ? (
                        <div className="project-onboard-table-wrap cpm-activities-table-wrap">
                            <table className="cpm-activities-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Stage</th>
                                        <th>Activity</th>
                                        <th className="text-right">Qty</th>
                                        <th>Unit</th>
                                        <th className="text-center">Duration</th>
                                        <th>ES</th>
                                        <th>EF</th>
                                        <th>LS</th>
                                        <th>LF</th>
                                        <th className="text-center">Float</th>
                                        <th>Gang</th>
                                        <th className="text-center">Critical</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredActivities.map((activity, idx) => (
                                        <tr 
                                            key={idx}
                                            className={activity.critical ? "critical-row" : ""}
                                            style={activity.color ? { borderLeft: `4px solid ${activity.color}` } : {}}
                                        >
                                            <td className="font-bold">{activity.id}</td>
                                            <td>
                                                <span 
                                                    className="stage-badge" 
                                                    style={activity.color ? { backgroundColor: activity.color } : {}}
                                                >
                                                    {activity.stage_label || activity.phase}
                                                </span>
                                            </td>
                                            <td className="font-bold">{activity.label}</td>
                                            <td className="text-right">{activity.quantity?.toFixed(2) || "-"}</td>
                                            <td>{activity.unit || "-"}</td>
                                            <td className="text-center font-bold">{activity.duration}</td>
                                            <td>{activity.es}</td>
                                            <td>{activity.ef}</td>
                                            <td>{activity.ls}</td>
                                            <td>{activity.lf}</td>
                                            <td className="text-center">
                                                <span className={`float-badge ${activity.total_float === 0 ? "float-zero" : ""}`}>
                                                    {activity.total_float}
                                                </span>
                                            </td>
                                            <td className="gang-cell">{activity.gang || "-"}</td>
                                            <td className="text-center">
                                                {activity.critical ? (
                                                    <span className="critical-icon">✓</span>
                                                ) : (
                                                    <span className="non-critical-icon">○</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <p className="text-muted">No activities match the selected filter.</p>
                        </div>
                    )}
                </div>
            </>
        );
    };

    const renderCPMNetworkTab = () => (
        <div className="cpm-network-tab">
            {/* Mother Network Diagram */}
            {cpmResult?.mother_diagram && (
                <div className="cpm-diagram-section mb-4">
                    <h3>Mother Network Diagram</h3>
                    <p className="text-muted">Full project CPM network</p>
                    <div className="network-diagram-wrapper text-center">
                        <a href={cpmResult.mother_diagram} target="_blank" rel="noopener noreferrer">
                            <img 
                                src={cpmResult.mother_diagram} 
                                alt="Mother CPM Network Diagram" 
                                className="img-fluid network-diagram-img"
                            />
                        </a>
                        <div className="mt-3">
                            <button 
                                type="button"
                                className="project-onboard-secondary pill"
                                onClick={() => window.open(cpmResult.mother_diagram, "_blank")}
                            >
                                Open in New Tab
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Stage Network Diagrams */}
            {cpmResult?.stage_diagrams && Object.keys(cpmResult.stage_diagrams).length > 0 && (
                <div className="cpm-diagram-section">
                    <h3>Stage Network Diagrams</h3>
                    <p className="text-muted">{Object.keys(cpmResult.stage_diagrams).length} stages</p>
                    <div className="stage-diagrams-grid">
                        {Object.entries(cpmResult.stage_diagrams).map(([stageLabel, diagramUrl], idx) => (
                            <div key={idx} className="stage-diagram-card">
                                <h6 className="font-weight-bold mb-2">{stageLabel}</h6>
                                <div className="stage-diagram-thumb">
                                    <img 
                                        src={diagramUrl} 
                                        alt={`${stageLabel} Network Diagram`} 
                                        className="img-fluid"
                                        onClick={() => window.open(diagramUrl, "_blank")}
                                        style={{ cursor: "pointer" }}
                                    />
                                </div>
                                <div className="mt-2 text-center">
                                    <button 
                                        type="button"
                                        className="project-onboard-secondary pill small"
                                        onClick={() => window.open(diagramUrl, "_blank")}
                                    >
                                        Open
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!cpmResult?.mother_diagram && (!cpmResult?.stage_diagrams || Object.keys(cpmResult.stage_diagrams).length === 0) && (
                <div className="text-center py-5">
                    <i className="fa fa-sitemap fa-3x text-muted mb-3"></i>
                    <h5>No Network Diagrams Available</h5>
                    <p className="text-muted">Run CPM analysis to generate network diagrams.</p>
                </div>
            )}
        </div>
    );

    const renderCriticalPathTab = () => {
        const criticalStages = cpmResult?.stages?.filter(s => s.critical) || [];
        const criticalActivities = cpmResult?.all_activities?.filter(a => a.critical) || [];
        const totalActivities = cpmResult?.all_activities?.length || 0;
        const totalStages = cpmResult?.stages?.length || 0;

        return (
            <div className="critical-path-tab">
                {/* Summary Cards */}
                <div className="cpm-summary-grid mb-4">
                    <div className="cpm-summary-card">
                        <span className="cpm-summary-label">Critical Path Stages</span>
                        <span className="cpm-summary-value text-danger">{criticalStages.length} / {totalStages}</span>
                    </div>
                    <div className="cpm-summary-card">
                        <span className="cpm-summary-label">Critical Activities</span>
                        <span className="cpm-summary-value text-danger">{criticalActivities.length} / {totalActivities}</span>
                    </div>
                    <div className="cpm-summary-card">
                        <span className="cpm-summary-label">Total Project Duration</span>
                        <span className="cpm-summary-value">{cpmResult?.total_days || 0} days</span>
                    </div>
                </div>

                {/* Critical Path Chain */}
                {cpmResult?.critical_path && cpmResult.critical_path.length > 0 && (
                    <div className="cpm-critical-path mb-4">
                        <h3>Critical Path Chain</h3>
                        <p className="text-muted">{cpmResult.critical_path.length} stages in sequence</p>
                        <div className="cpm-critical-path-stages">
                            {cpmResult.critical_path.map((stageId, index) => {
                                const stageDetail = cpmResult.stages?.find(s => s.stage_id === stageId);
                                return (
                                    <React.Fragment key={index}>
                                        <div className="critical-path-stage">
                                            <div 
                                                className="stage-phase-badge"
                                                style={{ backgroundColor: getPhaseColor(stageDetail?.phase || '') }}
                                            >
                                                {stageDetail?.phase || 'Stage'}
                                            </div>
                                            <div className="stage-label">{stageDetail?.stage_label || stageId}</div>
                                            <div className="stage-duration">{stageDetail?.duration || '-'} days</div>
                                            {stageDetail?.start && stageDetail?.end && (
                                                <div className="stage-dates text-muted">
                                                    {stageDetail.start} - {stageDetail.end}
                                                </div>
                                            )}
                                        </div>
                                        {index < cpmResult.critical_path.length - 1 && (
                                            <span className="cpm-arrow"> → </span>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Critical Activities Table */}
                {criticalActivities.length > 0 && (
                    <div className="cpm-activities-section">
                        <h3>Critical Activities (Zero Float)</h3>
                        <p className="text-muted">These activities directly impact the project completion date</p>
                        <div className="project-onboard-table-wrap cpm-activities-table-wrap">
                            <table className="cpm-activities-table">
                                <thead>
                                    <tr>
                                        <th>Stage</th>
                                        <th>Activity</th>
                                        <th className="text-center">Duration</th>
                                        <th>Early Start</th>
                                        <th>Early Finish</th>
                                        <th>Late Start</th>
                                        <th>Late Finish</th>
                                        <th className="text-center">Float</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {criticalActivities.map((activity, idx) => (
                                        <tr key={idx} className="critical-row">
                                            <td>
                                                <span className="stage-badge">
                                                    {activity.stage_label || activity.phase}
                                                </span>
                                            </td>
                                            <td className="font-bold">{activity.label}</td>
                                            <td className="text-center font-bold">{activity.duration}</td>
                                            <td>{activity.es}</td>
                                            <td>{activity.ef}</td>
                                            <td>{activity.ls}</td>
                                            <td>{activity.lf}</td>
                                            <td className="text-center">
                                                <span className="float-badge float-zero">{activity.total_float}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {criticalActivities.length === 0 && (
                    <div className="text-center py-5">
                        <i className="fa fa-check-circle fa-3x text-success mb-3"></i>
                        <h5>No Critical Activities</h5>
                        <p className="text-muted">All activities have positive float.</p>
                    </div>
                )}
            </div>
        );
    };

    const renderMilestonesTab = () => {
        const stageList = cpmResult?.stages || [];
        const phases = getUniquePhases();

        // Group milestones by phase
        const milestonesByPhase = {};
        stageList.forEach(stage => {
            const phase = stage.phase || 'Other';
            if (!milestonesByPhase[phase]) {
                milestonesByPhase[phase] = [];
            }
            milestonesByPhase[phase].push(stage);
        });

        return (
            <div className="milestones-tab">
                {/* Summary */}
                <div className="cpm-summary-grid mb-4">
                    <div className="cpm-summary-card">
                        <span className="cpm-summary-label">Total Milestones</span>
                        <span className="cpm-summary-value">{stageList.length * 2}</span>
                        <small className="text-muted">(Start + End for each stage)</small>
                    </div>
                    <div className="cpm-summary-card">
                        <span className="cpm-summary-label">Phases</span>
                        <span className="cpm-summary-value">{phases.length}</span>
                    </div>
                </div>

                {stageList.length > 0 ? (
                    <div className="milestones-timeline">
                        {Object.entries(milestonesByPhase).map(([phase, stages], phaseIdx) => (
                            <div key={phaseIdx} className="milestone-phase-section mb-4">
                                <div className="milestone-phase-header">
                                    <div 
                                        className="phase-icon"
                                        style={{ backgroundColor: getPhaseColor(phase) + '20', color: getPhaseColor(phase) }}
                                    >
                                        <i className="fa fa-flag-checkered"></i>
                                    </div>
                                    <h5 className="phase-title">{phase}</h5>
                                    <span className="phase-count">{stages.length} stages</span>
                                </div>

                                <div className="milestone-list">
                                    {stages.map((stage, idx) => (
                                        <div key={idx} className="milestone-item">
                                            <div className="milestone-connector">
                                                <div 
                                                    className={`milestone-dot ${stage.critical ? 'critical' : ''}`}
                                                    style={{ borderColor: getPhaseColor(phase) }}
                                                ></div>
                                                {idx < stages.length - 1 && (
                                                    <div 
                                                        className="milestone-line" 
                                                        style={{ backgroundColor: getPhaseColor(phase) + '40' }}
                                                    ></div>
                                                )}
                                            </div>
                                            <div className="milestone-content">
                                                <div className="milestone-header">
                                                    <h6 className="milestone-title">{stage.stage_label}</h6>
                                                    <div className="milestone-badges">
                                                        {stage.critical && (
                                                            <span className="badge-critical">Critical</span>
                                                        )}
                                                        <span className="badge-duration">
                                                            <i className="fa fa-clock-o mr-1"></i> {stage.duration} days
                                                        </span>
                                                    </div>
                                                </div>
                                                {stage.total_float !== undefined && (
                                                    <div className={`milestone-float ${stage.total_float === 0 ? 'float-zero' : ''}`}>
                                                        Float: {stage.total_float} days
                                                    </div>
                                                )}
                                                <div className="milestone-dates">
                                                    <div className="milestone-date">
                                                        <i className="fa fa-play-circle text-success"></i>
                                                        <span><strong>Start:</strong> {stage.start || '-'}</span>
                                                    </div>
                                                    <div className="milestone-date">
                                                        <i className="fa fa-check-circle text-primary"></i>
                                                        <span><strong>Finish:</strong> {stage.end || '-'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <i className="fa fa-flag-checkered fa-3x text-muted mb-3"></i>
                        <h5>No Milestones Available</h5>
                        <p className="text-muted">Stage data not available. Run CPM analysis first.</p>
                    </div>
                )}
            </div>
        );
    };

    const isCreatingProject = currentFloorIndex === 1 && !projectId;
    const startUploadButtonLabel = isCreatingProject ? "Create Project" : "Next";
    const startUploadSavingLabel = isCreatingProject ? "Creating..." : "Next...";

    // Determine which summary to display
    const activeCostSummary = customCostSummary || costSummary;

    return (
        <div className="project-onboard">
            <button type="button" className="project-onboard-back group" onClick={navigateBack}>
                <svg className="icon-small group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
                Back to Projects
            </button>

            {estimationLimitReached && !isEditMode && (
                <div className="plan-limit-warning-banner alert alert-warning mb-3 d-flex justify-content-between align-items-center">
                    <div>
                        <i className="fa fa-exclamation-triangle me-2"></i>
                        <span>You have reached the estimation limit of your plan. Please upgrade to run new estimations.</span>
                    </div>
                    <button type="button" className="btn btn-sm btn-warning" onClick={() => history.push('/plans/current')}>
                        Upgrade Plan
                    </button>
                </div>
            )}

            {projectLimitReached && !isEditMode && (
                <div className="plan-limit-warning-banner alert alert-danger mb-3 d-flex justify-content-between align-items-center">
                    <div>
                        <i className="fa fa-ban me-2"></i>
                        <span>You have reached your plan's project limit. Please upgrade your plan to create more projects.</span>
                    </div>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => history.push('/plans/current')}>
                        Upgrade Plan
                    </button>
                </div>
            )}

            {!cpmResult && currentStep === 1 && (
                <section className="project-onboard-progress-new">
                    <div className="project-onboard-horizontal-stepper">
                        <div className="project-onboard-step-item">
                            <div className="project-onboard-step-icon-wrapper active">
                                <svg className="project-onboard-step-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                                </svg>
                            </div>
                            <div className="project-onboard-step-line active" />
                            <div className="project-onboard-step-number active">1</div>
                            <span className="project-onboard-step-label">Select Module</span>
                        </div>

                        <div className="project-onboard-step-item">
                            <div className="project-onboard-step-icon-wrapper">
                                <svg className="project-onboard-step-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                            </div>
                            <div className="project-onboard-step-line" />
                            <div className="project-onboard-step-number">2</div>
                            <span className="project-onboard-step-label">Upload Drawings</span>
                        </div>

                        <div className="project-onboard-step-item">
                            <div className="project-onboard-step-icon-wrapper">
                                <span className="project-onboard-step-rupee">₹</span>
                            </div>
                            <div className="project-onboard-step-line" />
                            <div className="project-onboard-step-number">3</div>
                            <span className="project-onboard-step-label">Review & Pay</span>
                        </div>

                        <div className="project-onboard-step-item last">
                            <div className="project-onboard-step-icon-wrapper">
                                <svg className="project-onboard-step-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div className="project-onboard-step-number">4</div>
                            <span className="project-onboard-step-label">Summary</span>
                        </div>
                    </div>
                </section>
            )}

            {!cpmResult && currentStep > 1 && (
                <section className="project-onboard-progress">
                    <div className="project-onboard-stepper">
                        <div className={`project-onboard-step ${currentStep >= 1 ? "active" : ""}`}>
                            <svg className="project-onboard-step-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                            </svg>
                        </div>
                        <div className={`project-onboard-line ${currentStep > 1 ? "active" : ""}`} />
                        <div className={`project-onboard-step ${(currentStep >= 2 && currentStep <= totalWizardSteps + 1) || currentStep === transitionStepNumber ? "active" : ""}`}>
        <svg className="project-onboard-step-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
    </div>
                        <div className={`project-onboard-line ${stepState === "cost-summary" || stepState === "cpm-date-input" || stepState === "cpm-result" ? "active" : ""}`} />
                        <div className={`project-onboard-step ${stepState === "cost-summary" || stepState === "cpm-date-input" || stepState === "cpm-result" ? "active" : ""}`}>₹</div>
                        <div className={`project-onboard-line ${stepState === "cpm-date-input" || stepState === "cpm-result" ? "active" : ""}`} />
                        <div className={`project-onboard-step ${stepState === "cpm-date-input" || stepState === "cpm-result" ? "active" : ""}`}>
                            <svg className="project-onboard-step-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                            </svg>
                        </div>
                    </div>

                    <div className="project-onboard-meter">
                        <span style={{ width: `${progress}%` }} />
                    </div>

                    <p>
                        {currentStep === transitionStepNumber && <>Step {totalStepsInTitle} of {totalStepsInTitle}: <strong>Floor Transition</strong></>}
                        {currentStep > 1 && currentStep !== transitionStepNumber && stepState !== "cpm-date-input" && stepState !== "cpm-result" && (
                            <><span className="module-badge">{currentUploadStep?.title}</span> Floor {currentFloorIndex} of {floorsCount} · Step {currentStep} of {totalStepsInTitle}</>
                        )}
                        {(stepState === "cpm-date-input" || stepState === "cpm-result") && <>Step: <strong>CPM Schedule</strong></>}
                    </p>
                </section>
            )}

            <main className="project-onboard-panel">
                {errorStatus && <div className="project-onboard-error" ref={errorRef}>{typeof errorStatus === 'string' ? errorStatus : JSON.stringify(errorStatus)}</div>}
                {saveSuccessMessage && <div className="project-onboard-floor-note">{saveSuccessMessage}</div>}

                {currentStep === 1 && (
                    <form onSubmit={handleStartUpload}>
                        {/* <div className="project-onboard-title-row">
                            <div className="project-onboard-upload-icon">
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                </svg>
                            </div>
                            <div>
                                <h2>Upload Construction Drawings</h2>
                                <p>Upload drawings for foundation, structure, plan, elevation, lintel, staircase, and floor modules.</p>
                            </div>
                        </div> */}

                        {floorsCount > 1 && (
                            <div className="project-onboard-floor-note">
                                Active Floor Target: <strong>Floor {currentFloorIndex}</strong> of {floorsCount}
                            </div>
                        )}

                        {currentFloorIndex === 1 ? (
                            <>
                                <div className="project-onboard-step-title">
                                    <h3>Step 1 of 4: <span className="step-highlight">Select Module</span></h3>
                                </div>

                                <div className="project-onboard-form-grid-new">
                                    {/* Row 1: Site and Client */}
                                    <div className="project-onboard-form-field">
                                        <label className="project-onboard-field-label">
                                            <div className="label-with-icon">
                                                <svg className="field-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                <span className="label-text">SELECT SITE <span className="required-star">*</span></span>
                                            </div>
                                        </label>
                                        <div className="select-wrapper">
                                            {loadingSites ? (
                                                <Select
                                                    isDisabled
                                                    placeholder="Loading sites..."
                                                    styles={customSelectStyles}
                                                    menuPortalTarget={document.body}
                                                    classNamePrefix="react-select"
                                                />
                                            ) : sites.length > 0 ? (
                                                <Select
                                                    value={selectedSiteId ? sites.find(s => String(s.id) === String(selectedSiteId)) ? { value: selectedSiteId, label: `${sites.find(s => String(s.id) === String(selectedSiteId)).name}${sites.find(s => String(s.id) === String(selectedSiteId)).location ? ` (${sites.find(s => String(s.id) === String(selectedSiteId)).location})` : ''}` } : null : null}
                                                    onChange={(option) => setSelectedSiteId(option?.value || "")}
                                                    options={sites.map(site => ({ value: site.id, label: `${site.name}${site.location ? ` (${site.location})` : ''}` }))}
                                                    placeholder="Choose a site for this project"
                                                    isClearable
                                                    styles={customSelectStyles}
                                                    menuPortalTarget={document.body}
                                                    classNamePrefix="react-select"
                                                />
                                            ) : (
                                                <div className="project-onboard-no-data">
                                                    <span>No sites found</span>
                                                    <button type="button" className="project-onboard-create-btn" onClick={() => history.push('/sites/add')}>
                                                        Create Site
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="project-onboard-form-field">
                                        <label className="project-onboard-field-label">
                                            <div className="label-with-icon">
                                                <svg className="field-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                <span className="label-text">SELECT CLIENT <span className="required-star">*</span></span>
                                            </div>
                                        </label>
                                        <div className="select-wrapper">
                                            {loadingClients ? (
                                                <Select
                                                    isDisabled
                                                    placeholder="Loading clients..."
                                                    styles={customSelectStyles}
                                                    menuPortalTarget={document.body}
                                                    classNamePrefix="react-select"
                                                />
                                            ) : clients.length > 0 ? (
                                                <Select
                                                    value={selectedClientId ? clients.find(c => String(c.id) === String(selectedClientId)) ? { value: selectedClientId, label: `${clients.find(c => String(c.id) === String(selectedClientId)).name} (${clients.find(c => String(c.id) === String(selectedClientId)).contact})` } : null : null}
                                                    onChange={(option) => setSelectedClientId(option?.value || "")}
                                                    options={clients.map(client => ({ value: client.id, label: `${client.name} (${client.contact})` }))}
                                                    placeholder="Choose a client for this project"
                                                    isClearable
                                                    isDisabled={!selectedSiteId}
                                                    styles={customSelectStyles}
                                                    menuPortalTarget={document.body}
                                                    classNamePrefix="react-select"
                                                />
                                            ) : !selectedSiteId ? (
                                                <div className="project-onboard-no-data">
                                                    <span>Please select a site first</span>
                                                </div>
                                            ) : (
                                                <div className="project-onboard-no-data">
                                                    <span>No clients found</span>
                                                    <button type="button" className="project-onboard-create-btn" onClick={() => history.push('/clients/add')}>
                                                        Create Client
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 2: Subscription and Project Name */}
                                    <div className="project-onboard-form-field">
                                        <label className="project-onboard-field-label">
                                            <div className="label-with-icon">
                                                <svg className="field-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <span className="label-text">SELECT SUBSCRIPTION <span className="required-star">*</span></span>
                                            </div>
                                        </label>
                                        <div className="select-wrapper">
                                            {loadingSubscriptions ? (
                                                <Select
                                                    isDisabled
                                                    placeholder="Loading subscriptions..."
                                                    styles={customSelectStyles}
                                                    menuPortalTarget={document.body}
                                                    classNamePrefix="react-select"
                                                />
                                            ) : filteredSubscriptions.length > 0 ? (
                                                <Select
                                                    value={selectedSubscriptionId ? filteredSubscriptions.find(s => String(s.id) === String(selectedSubscriptionId)) ? { value: selectedSubscriptionId, label: `${filteredSubscriptions.find(s => String(s.id) === String(selectedSubscriptionId)).planName} (${filteredSubscriptions.find(s => String(s.id) === String(selectedSubscriptionId)).availableCount} available)` } : null : null}
                                                    onChange={(option) => setSelectedSubscriptionId(option?.value || "")}
                                                    options={filteredSubscriptions.map(sub => ({ value: sub.id, label: `${sub.planName} (${sub.availableCount} available)` }))}
                                                    placeholder="Choose a subscription plan"
                                                    isClearable
                                                    styles={customSelectStyles}
                                                    menuPortalTarget={document.body}
                                                    classNamePrefix="react-select"
                                                />
                                            ) : (
                                                <div className="project-onboard-no-data">
                                                    <span>No active subscriptions</span>
                                                    <button type="button" className="project-onboard-create-btn" onClick={() => history.push('/plans')}>
                                                        View Plans
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="project-onboard-form-field">
                                        <label className="project-onboard-field-label">
                                            <div className="label-with-icon">
                                                <svg className="field-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                                <span className="label-text">PROJECT NAME <span className="required-star">*</span></span>
                                            </div>
                                        </label>
                                        <input
                                            type="text"
                                            value={projectName}
                                            onChange={(event) => setProjectName(event.target.value)}
                                            placeholder="e.g., Residential Building - Phase 1"
                                            className="project-onboard-input"
                                        />
                                    </div>

                                    {/* Row 3: Number of Floors */}
                                    <div className="project-onboard-form-field">
                                        <label className="project-onboard-field-label">
                                            <div className="label-with-icon">
                                                <svg className="field-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                                </svg>
                                                <span className="label-text">NUMBER OF FLOORS <span className="required-star">*</span></span>
                                            </div>
                                        </label>
                                        <div className="select-wrapper">
                                            <Select
                                                value={{ value: floorsCount, label: `${floorsCount} Floor${floorsCount > 1 ? 's' : ''}` }}
                                                onChange={(option) => setFloorsCount(Number(option?.value || 1))}
                                                options={Array.from({ length: maxFloorsAllowed }, (_, i) => i + 1).map(num => ({ value: num, label: `${num} Floor${num > 1 ? 's' : ''}` }))}
                                                placeholder="Select number of floors"
                                                isSearchable={false}
                                                isDisabled={!selectedSubscriptionId || loadingPlanDetails}
                                                styles={customSelectStyles}
                                                menuPortalTarget={document.body}
                                                classNamePrefix="react-select"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="project-onboard-info-display">
                                <div className="project-onboard-info-item">
                                    <span className="info-label">Client:</span>
                                    <span className="info-value">
                                        {clients.find(c => c.id === selectedClientId)?.name || "N/A"} 
                                        {clients.find(c => c.id === selectedClientId)?.contact && 
                                            ` (${clients.find(c => c.id === selectedClientId)?.contact})`
                                        }
                                    </span>
                                </div>
                                <div className="project-onboard-info-item">
                                    <span className="info-label">Project Name:</span>
                                    <span className="info-value">{projectName || "N/A"}</span>
                                </div>
                                <div className="project-onboard-info-item">
                                    <span className="info-label">Number of Floors:</span>
                                    <span className="info-value">{floorsCount} {floorsCount === 1 ? "Floor" : "Floors"}</span>
                                </div>
                            </div>
                        )}

                        <div className="project-onboard-selection-head">
                            <h3>
                                {selectedSubscriptionId ? "Select Blueprint Drawing Types to Upload" : "Select a Subscription to Enable Drawing Selection"}
                            </h3>
                            <span>{selectedDrawingTypeIds.length} type(s) selected</span>
                        </div>

                        <div className="project-onboard-card-grid">
                            {drawingTypes.map(card => {
                                const selected = selectedDrawingTypeIds.includes(card.id);
                                const lockedType = ["superstructure", "elevation", "lintel-beam", "staircase"].includes(card.id);
                                const notForUpperFloor = currentFloorIndex > 1 && ["foundation", "plinth-beam"].includes(card.id);
                                const coreIds = currentFloorIndex > 1
                                    ? ["floor-beam", "floor-slab", "columns"]
                                    : ["foundation", "plinth-beam", "floor-beam", "floor-slab", "columns"];
                                const allCoreSelected = coreIds.every(id => selectedDrawingTypeIds.includes(id));
                                const disabled = !selectedSubscriptionId || notForUpperFloor || (lockedType && !allCoreSelected);
                                const cardDescription = card.id === "columns" && !selectedDrawingTypeIds.includes("floor-beam")
                                    ? `${card.description} Requires Floor Beam drawing.`
                                    : card.description;

                                return (
                                    <button
                                        type="button"
                                        key={card.id}
                                        onClick={() => handleToggleCard(card.id)}
                                        className={`project-onboard-type-card ${selected ? "selected" : ""} ${disabled ? "disabled" : ""}`}
                                        disabled={disabled}
                                    >
                                        <div className="project-onboard-card-top">
                                            <span className="project-onboard-card-mark">{renderCardIcon(card.id, selected)}</span>
                                            <span className="project-onboard-check">{selected && !disabled ? "✓" : disabled ? "" : ""}</span>
                                        </div>
                                        <strong>{card.title}</strong>
                                        <p>{disabled ? (notForUpperFloor ? "Not required for upper floors." : !selectedSubscriptionId ? "Select a subscription first." : "Complete initial structural selection first.") : cardDescription}</p>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="project-onboard-actions">
                            <button type="button" className="project-onboard-secondary" onClick={navigateBack}>Cancel</button>
                            <button type="submit" className="project-onboard-primary" disabled={saving || (isCreatingProject && estimationLimitReached)}>
                                {saving ? startUploadSavingLabel : startUploadButtonLabel}
                            </button>
                        </div>
                    </form>
                )}

                {currentStep >= 2 && currentStep <= totalWizardSteps + 1 && stepState === "upload" && (
                    <form onSubmit={handleNextStep}>
                        {/* <div className="project-onboard-title-row">
                            <div className="project-onboard-title-icon purple">
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            </div>
                            <div>
                                <h2>Upload {currentUploadStep?.displayId} Diagram</h2>
                                <p>Upload image, or PDF.</p>
                            </div>
                        </div> */}





                        <div
                            className={`project-onboard-dropzone ${isDragging && !saving ? "dragging" : ""} ${saving ? "disabled" : ""}`}
                            onClick={() => {
                                if (saving) return;
                                document.getElementById(`project-onboard-file-${currentUploadStep?.id}`)?.click();
                            }}
                            onDragOver={(event) => {
                                event.preventDefault();
                                if (saving) return;
                                setIsDragging(true);
                            }}
                            onDragLeave={(event) => {
                                event.preventDefault();
                                setIsDragging(false);
                            }}
                            onDrop={(event) => {
                                event.preventDefault();
                                setIsDragging(false);
                                if (saving) return;
                                addFiles(Array.from(event.dataTransfer.files || []));
                            }}
                        >
                            <input
                                id={`project-onboard-file-${currentUploadStep?.id}`}
                                type="file"
                                multiple
                                accept="image/*,application/pdf"
                                disabled={saving}
                                onChange={(event) => addFiles(Array.from(event.target.files || []))}
                            />
                            <div className="project-onboard-drop-icon">
                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                            </div>
                            {saving ? (
                                <>
                                    <strong>Uploading &amp; estimating...</strong>
                                    <span>Please wait while the AI prepares your estimate.</span>
                                </>
                            ) : (
                                <>
                                    <strong>Drop your diagram here</strong>
                                    <span>or click to browse (JPEG, PNG, WEBP, PDF)</span>
                                </>
                            )}
                        </div>

                        <div className="project-onboard-upload-head">
                            <strong>Total Files Select List</strong>
                            <span>{uploadedFiles.length} file{uploadedFiles.length === 1 ? "" : "s"} added</span>
                        </div>

                        {uploadedFiles.length ? (
                            <div className="project-onboard-preview-scroll-container">
                                <div className="project-onboard-preview-grid">
                                    {uploadedFiles.map(file => (
                                        <div className={`project-onboard-preview ${saving ? "scanning" : ""}`} key={file.id}>
                                            <div className="project-onboard-preview-media">
                                                {file.url ? (
                                                    <img src={file.url} alt={file.name} />
                                                ) : (
                                                    <span>PDF</span>
                                                )}
                                                {saving ? (
                                                    <span className="project-onboard-scan-line" aria-hidden="true" />
                                                ) : (
                                                    <button type="button" onClick={() => removeFile(file.id)} title="Delete drawing">x</button>
                                                )}
                                            </div>
                                            <div className="project-onboard-preview-info">
                                                <strong title={file.name}>{file.name}</strong>
                                                <span>{file.size}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="project-onboard-empty">
                                <strong>No files added to the queue yet</strong>
                                <span>Select or drop genuine drawing files to begin estimation.</span>
                            </div>
                        )}

                        {saving && (
                            <div className="project-onboard-progress-bar-wrapper" aria-live="polite" role="status">
                                <div className="project-onboard-progress-head">
                                    <strong>Estimation in progress</strong>
                                    <span className="project-onboard-elapsed">
                                        Running for {formatElapsed(elapsedSeconds)} · typically takes 1–2 min
                                    </span>
                                </div>

                                <ul className="project-onboard-stage-list">
                                    {estimationStages.map((stage, index) => {
                                        const status = index < activeStageIndex
                                            ? "done"
                                            : index === activeStageIndex
                                                ? "active"
                                                : "pending";
                                        return (
                                            <li key={stage.id} className={`project-onboard-stage ${status}`}>
                                                <span className="stage-marker">
                                                    {status === "done" ? "✓" : status === "active" ? <span className="stage-spinner" /> : index + 1}
                                                </span>
                                                <span className="stage-label">{stage.label}</span>
                                            </li>
                                        );
                                    })}
                                </ul>

                                <div className="project-onboard-progress-bar-container">
                                    <div
                                        className="project-onboard-progress-bar-fill"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                    <span className="project-onboard-progress-percentage">
                                        {uploadProgress}%
                                    </span>
                                    {uploadProgress >= 60 && uploadProgress < 100 && (
                                        <div className="project-onboard-progress-shimmer" aria-hidden="true" />
                                    )}
                                </div>

                                {uploadProgressMessage && (
                                    <p className="project-onboard-progress-message">
                                        {uploadProgressMessage}
                                    </p>
                                )}

                                {uploadProgress >= 60 && uploadProgress < 100 && (
                                    <p className="project-onboard-progress-tip">
                                        {estimationWaitingTips[tipIndex % estimationWaitingTips.length]}
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="project-onboard-actions">
                            <button type="button" className="project-onboard-secondary" onClick={handlePrevStep} disabled={saving}>Back</button>
                            <button type="submit" className="project-onboard-primary" disabled={saving || estimationLimitReached}>
                                {saving ? "Processing..." : "Upload and Estimate ->"}
                            </button>
                        </div>
                    </form>
                )}

                {currentStep >= 2 && currentStep <= totalWizardSteps + 1 && stepState === "results" && (() => {
                    const result = currentResult || {};
                    const rows = result.rows || [];
                    return (
                        <section>
                            <div className="project-onboard-results-head">
                                <div className="project-onboard-title-row compact">
                                   
                                    <div>
                                        <h2>Estimation</h2>
                                       
                                    </div>
                                </div>
                                <button type="button" className="project-onboard-secondary pill" onClick={() => downloadResult(result, rows, currentUploadStep?.displayId || "module")}>
                                    Download Excel
                                </button>
                               </div>
  

                            <div className="project-onboard-table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Quantity</th>
                                            <th>Unit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length ? rows.map((row, index) => (
                                            <tr key={`${row.item}-${index}`}>
                                                <td>{row.item}</td>
                                                <td>{String(row.quantity ?? "-")}</td>
                                                <td>{row.unit || "-"}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="3">No tabular metrics returned for this module.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="project-onboard-actions">
                                <button type="button" className="project-onboard-secondary" onClick={handlePrevStep}>Modify Diagrams Upload</button>
                                <button
                                    type="button"
                                    className="project-onboard-primary wide"
                                    onClick={() => {
                                        if (currentStep === totalWizardSteps + 1) {
                                            if (hasTransitionStep) {
                                                setCurrentStep(transitionStepNumber);
                                                setStepState("upload");
                                            } else {
                                                calculateProjectCost();
                                            }
                                        } else {
                                            setCurrentStep(prev => prev + 1);
                                            setStepState("upload");
                                        }
                                    }}
                                    disabled={saving || calculatingCost}
                                >
                                    {calculatingCost ? "Calculating..." : saving ? "Saving..." : currentStep === totalWizardSteps + 1 ? (hasTransitionStep ? "Next Floor >" : "Calculate Cost >") : `Continue to ${activeUploadSteps[currentStep - 1]?.title || "Next Step"} >`}
                                </button>
                            </div>
                        </section>
                    );
                })()}

                {stepState === "cost-summary" && costSummary && (
                    <section className="project-onboard-cost-summary">
                        <div className="project-onboard-cost-overview">
                            <div className="project-onboard-title-row compact">
                                <div className="project-onboard-title-icon success">₹</div>
                                <div>
                                    <h2>Cost Breakdown</h2>
                                    <p>Detailed cost in INR</p>
                                    <div className="cost-radio-group">
                                        <label className="cost-radio-label">
                                            <input
                                                type="radio"
                                                name="costType"
                                                value="custom"
                                                checked={costTab === 'custom'}
                                                onChange={() => handleTabSwitch('custom')}
                                            />
                                            <span>Fixed Cost</span>
                                        </label>
                                        <label className="cost-radio-label">
                                            <input
                                                type="radio"
                                                name="costType"
                                                value="material"
                                                checked={costTab === 'material'}
                                                onChange={() => handleShowMaterialCost()}
                                            />
                                            <svg className="cost-toggle-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                            <span>Material Cost</span>
                                        </label>
                                        <label className="cost-radio-label">
                                            <input
                                                type="radio"
                                                name="costType"
                                                value="labour"
                                                checked={costTab === 'labour'}
                                                onChange={() => handleShowLabourCost()}
                                            />
                                            <svg className="cost-toggle-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            <span>Labour Cost</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="project-onboard-secondary pill" onClick={downloadCostSummary}>
                                Download Excel
                            </button>
                        </div>

                        {costTab === 'custom' && (
                            <div className="cost-custom-actions">
                                <span className="cost-custom-status">
                                    {ratesAreDefault ? (
                                        <span className="cost-status-default"></span>
                                    ) : (
                                        <span className="cost-status-custom">Using project-specific custom rates</span>
                                    )}
                                </span>
                                <div className="cost-custom-buttons">
                                    <button
                                        type="button"
                                        className="project-onboard-secondary"
                                        onClick={toggleCustomTableEdit}
                                    >
                                        {isCustomTableEditing ? "Lock Rates" : "Click to Edit Cost"}
                                    </button>
                                    <button
                                        type="button"
                                        className="project-onboard-secondary"
                                        onClick={handleResetCustomRates}
                                        disabled={savingCustomRates || ratesAreDefault || fetchingRates}
                                    >
                                        {savingCustomRates ? "Resetting..." : "Reset to Default"}
                                    </button>
                                    <button
                                        type="button"
                                        className="project-onboard-primary"
                                        onClick={handleSaveCustomRates}
                                        disabled={savingCustomRates || fetchingRates}
                                    >
                                        {savingCustomRates ? "Saving..." : "Save Custom Rates"}
                                    </button>
                                </div>
                            </div>
                        )}



                        {/* ---------- Material Cost Inline Table ---------- */}
                        {costTab === 'material' && (
                            <div className="project-onboard-table-wrap cost-table">
                                <div className="cost-table-actions">
                                    <button
                                        type="button"
                                        className="project-onboard-secondary"
                                        onClick={toggleMaterialTableEdit}
                                    >
                                        {isMaterialTableEditing ? "Lock Rates" : "Click to Edit Cost"}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="project-onboard-secondary"
                                        onClick={handleResetMaterialRates}
                                        disabled={savingCustomRates}
                                    >
                                        {savingCustomRates ? "Resetting..." : "Reset to Default"}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="project-onboard-primary"
                                        onClick={handleSaveMaterialRates}
                                        disabled={savingCustomRates}
                                    >
                                        {savingCustomRates ? "Saving..." : "Save Material Rates"}
                                    </button>
                                </div>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th className="rate-header-with-toggle">
                                                <span>Rate</span>
                                           
                                            </th>
                                            <th>Unit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingMaterialRates ? (
                                            <tr>
                                                <td colSpan="3" className="text-center">Loading material rates...</td>
                                            </tr>
                                        ) : materialRates.length === 0 ? (
                                            <tr>
                                                <td colSpan="3" className="text-center">No material rates available</td>
                                            </tr>
                                        ) : (
                                            materialRates.map((item, index) => {
                                                const isEditingMat = isMaterialTableEditing;
                                                return (
                                                <tr key={index} className="cost-item-row">
                                                    <td className="item-cell">{item.label}</td>
                                                    <td className="rate-cell-no-toggle">
                                                        {isEditingMat ? (
                                                            <input type="number" className="cost-rate-input" value={item.rate} onChange={(e) => handleMaterialRateChange(item.label, e.target.value)} min="0" step="0.01" />
                                                        ) : (
                                                            <span className="rate-display-value">{item.rate}</span>
                                                        )}
                                                    </td>
                                                    <td>{item.unit}</td>
                                                </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                                
                            </div>
                        )}
                        {/* ---------- Labour Cost Inline Table ---------- */}
                        {costTab === 'labour' && (
                            <div className="project-onboard-table-wrap cost-table">
                                  <div className="cost-table-actions">
                                    <button
                                        type="button"
                                        className="project-onboard-secondary"
                                        onClick={toggleLabourTableEdit}
                                    >
                                        {isLabourTableEditing ? "Lock Rates" : "Click to Edit Cost"}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="project-onboard-secondary"
                                        onClick={handleResetLabourRates}
                                        disabled={savingCustomRates}
                                    >
                                        {savingCustomRates ? "Resetting..." : "Reset to Default"}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="project-onboard-primary"
                                        onClick={handleSaveLabourRates}
                                        disabled={savingCustomRates}
                                    >
                                        {savingCustomRates ? "Saving..." : "Save Labour Rates"}
                                    </button>
                                </div>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th className="rate-header-with-toggle">
                                                <span>Rate</span>
                                               
                                            </th>
                                            <th>Unit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingLabourRates ? (
                                            <tr>
                                                <td colSpan="3" className="text-center">Loading labour rates...</td>
                                            </tr>
                                        ) : labourRates.length === 0 ? (
                                            <tr>
                                                <td colSpan="3" className="text-center">No labour rates available</td>
                                            </tr>
                                        ) : (
                                            labourRates.map((item, index) => {
                                                const isEditingLab = isLabourTableEditing;
                                                return (
                                                <tr key={index} className="cost-item-row">
                                                    <td className="item-cell">{item.label}</td>
                                                    <td className="rate-cell-no-toggle">
                                                        {isEditingLab ? (
                                                            <input type="number" className="cost-rate-input" value={item.rate} onChange={(e) => handleLabourRateChange(item.label, e.target.value)} min="0" step="0.01" />
                                                        ) : (
                                                            <span className="rate-display-value">{item.rate}</span>
                                                        )}
                                                    </td>
                                                    <td>{item.unit}</td>
                                                </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                              
                            </div>
                        )}

                    

                        {/* ---------- Default / Custom Cost Table ---------- */}
                        {costTab === 'custom' && (
                            <div className="project-onboard-table-wrap cost-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Unit</th>
                                            <th className="text-right">Quantity</th>
                                            <th className="text-right rate-header-with-toggle">
                                                <span>Rate (₹)</span>
                                                 
                                            </th>
                                            <th className="text-right">Amount (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeCostSummary?.rows && activeCostSummary.rows.map((row, index) => {
                                            const isFloorBanner = row.type === 'floor';
                                            const isModuleBanner = row.type === 'module';
                                            const isModuleSubtotal = row.type === 'module_subtotal';
                                            const isFloorTotal = row.type === 'floor_total';
                                            const isGrandTotal = row.type === 'grand_total';
                                            const isDataRow = row.type === 'item';
                                            
                                            if (isFloorBanner) {
                                                return (
                                                    <tr key={index} className="floor-banner">
                                                        <td colSpan="5">{row["Floor / Module"]}</td>
                                                    </tr>
                                                );
                                            }
                                            
                                            if (isModuleBanner) {
                                                return (
                                                    <tr key={index} className="module-banner">
                                                        <td colSpan="5">{row["Floor / Module"]}</td>
                                                    </tr>
                                                );
                                            }
                                            
                                            if (isModuleSubtotal || isFloorTotal) {
                                                return (
                                                    <tr key={index} className={isFloorTotal ? "floor-total-row" : "subtotal-row"}>
                                                        <td colSpan="4" className="text-right"><strong>{row.Item}</strong></td>
                                                        <td className="text-right"><strong>{formatINR(row["Amount (₹)"] || 0)}</strong></td>
                                                    </tr>
                                                );
                                            }
                                            
                                            if (isGrandTotal) {
                                                return (
                                                    <tr key={index} className="grand-total-row">
                                                        <td colSpan="4" className="text-right"><strong>{row.Item}</strong></td>
                                                        <td className="text-right"><strong>{formatINR(row["Amount (₹)"] || 0)}</strong></td>
                                                    </tr>
                                                );
                                            }
                                            
                                            // Data row - render based on tab
                                            if (isDataRow) {
                                                // Get the current rate - from customRates if in custom tab, otherwise from row
                                                const currentRate = costTab === 'custom' && customRates[row.Item] !== undefined 
                                                    ? customRates[row.Item] 
                                                    : (row["Rate (₹)"] || 0);
                                                
                                                // Calculate amount with current rate
                                                const qty = Number(row.Quantity) || 0;
                                                const calculatedAmount = qty * Number(currentRate);
                                                
                                                // Check if rate has changed from original
                                                const originalRate = row["Rate (₹)"] || 0;
                                                const hasChanged = Number(currentRate) !== Number(originalRate);
                                                
                                                // CUSTOM TAB: Render with editable input
                                                // Use rateInputStrings if available, otherwise fallback to currentRate or original rate
                                                let displayRate = rateInputStrings[row.Item];
                                                if (displayRate === undefined || displayRate === null) {
                                                    // Fallback: use customRates or original rate
                                                    displayRate = customRates[row.Item] !== undefined 
                                                        ? String(customRates[row.Item]) 
                                                        : String(row["Rate (₹)"] || 0);
                                                }
                                                
                                                const isEditingCustom = isCustomTableEditing;
                                                
                                                return (
                                                    <tr key={index} className="cost-item-row">
                                                        <td className="item-cell">{row.Item || ''}</td>
                                                        <td><span className="unit-badge">{row.Unit || '-'}</span></td>
                                                        <td className="text-right">{row.Quantity !== undefined && row.Quantity !== null ? row.Quantity : ''}</td>
                                                        <td className="text-right rate-cell-no-toggle">
                                                            {isEditingCustom ? (
                                                                <input
                                                                    type="number"
                                                                    className="cost-rate-input"
                                                                    value={displayRate}
                                                                    onChange={(e) => handleRateChange(row.Item, e.target.value)}
                                                                    min="0"
                                                                    step="0.01"
                                                                />
                                                            ) : (
                                                                <span className="rate-display-value">{displayRate}</span>
                                                            )}
                                                        </td>
                                                        <td className="text-right amount-cell">
                                                            <span className={hasChanged ? 'amount-changed' : ''}>
                                                                {formatINR(calculatedAmount)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                            
                                            // Should not reach here for valid rows
                                            return null;
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="project-onboard-actions">
                            <button type="button" className="project-onboard-secondary" onClick={() => setStepState("results")}>
                                Back to Results
                            </button>
                            <button type="button" className="project-onboard-primary" onClick={finishProject} disabled={saving}>
                                {saving ? "Finishing..." : "Finish Project"}
                            </button>
                            <button type="button" className="project-onboard-primary wide" onClick={handleRunCpm}>
                                Run CPM
                            </button>
                        </div>

                    </section>
                )}

                {stepState === "cpm-date-input" && (
                    <section className="project-onboard-cost-summary">
                        <div className="project-onboard-cost-overview">
                            <div className="project-onboard-title-row compact">
                                <div className="project-onboard-title-icon success">
                                    <svg className="project-onboard-step-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                    </svg>
                                </div>
                                <div>
                                    <h2>CPM Schedule Dates</h2>
                                    <p>Set the start date and optionally the target end date for CPM analysis</p>
                                </div>
                            </div>
                        </div>

                        <div className="project-onboard-bg-white">
                            <div className="cpm-date-fields-row">
                                <div className="cpm-date-field">
                                    <label>Start Date *</label>
                                    <input
                                        type="date"
                                        value={cpmStartDate}
                                        onChange={(e) => setCpmStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="cpm-date-field">
                                    <label>End Date (optional)</label>
                                    <input
                                        type="date"
                                        value={cpmEndDate}
                                        onChange={(e) => setCpmEndDate(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="cpm-date-section-actions">
                            <button type="button" className="project-onboard-secondary" onClick={handleBackFromDateInput}>
                                    Back to Cost Breakdown
                                </button>
                                <button
                                    type="button"
                                    className="project-onboard-primary wide"
                                    onClick={handleGenerateCpm}
                                    disabled={cpmLoading}
                                >
                                    {cpmLoading ? "Generating CPM Schedule..." : "Generate CPM Schedule"}
                                </button>
                                
                            </div>
                        </div>
                    </section>
                )}

                {stepState === "cpm-result" && cpmResult && (
                    <section className="project-onboard-cost-summary">
                        <div className="project-onboard-cost-overview">
                            <div className="project-onboard-title-row compact">
                                <div className="project-onboard-title-icon success">CPM</div>
                                <div>
                                    <h2>CPM Schedule Results</h2>
                                    <p>Critical Path Method analysis for project schedule</p>
                                </div>
                            </div>
                            <div className="cpm-overview-actions">
                                {cpmResult.excel_url && (
                                    <button
                                        type="button"
                                        className="project-onboard-secondary pill"
                                        onClick={() => downloadUrlWithPrompt(cpmResult.excel_url, `${projectName.trim().replace(/\s+/g, "_") || "Project"}_CPM_Results.xlsx`)}
                                    >
                                        Download Excel
                                    </button>
                                )}
                                {(cpmResult.mother_diagram || cpmResult.stage_diagrams) && (
                                    <button
                                        type="button"
                                        className="project-onboard-secondary pill"
                                        onClick={downloadAllDiagrams}
                                    >
                                        Download All Diagrams (ZIP)
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Summary Cards */}
                        <div className="cpm-summary-grid">
                            {/* <div className="cpm-summary-card">
                                <span className="cpm-summary-label">Status</span>
                                <span className="cpm-summary-value">{cpmResult.status || "N/A"}</span>
                            </div> */}
                          
                            <div className="cpm-summary-card">
                                <span className="cpm-summary-label">Start Date</span>
                                <span className="cpm-summary-value">{cpmResult.start_date || cpmStartDate || "N/A"}</span>
                            </div>
                            <div className="cpm-summary-card">
                                <span className="cpm-summary-label">End Date</span>
                                <span className="cpm-summary-value">{cpmResult.end_date || "N/A"}</span>
                            </div>
                            <div className="cpm-summary-card">
                                <span className="cpm-summary-label">Total Days</span>
                                <span className="cpm-summary-value">{cpmResult.total_days || "N/A"}</span>
                            </div>
                            {/* <div className="cpm-summary-card">
                                <span className="cpm-summary-label">Feasibility</span>
                                <span className={`cpm-summary-value ${cpmResult.feasibility === "Feasible" ? "cpm-feasible" : "cpm-not-feasible"}`}>
                                    {cpmResult.feasibility || "N/A"}
                                </span>
                            </div> */}
                        </div>

                        {/* Warning */}
                        {cpmResult.warning && (
                            <div className="cpm-warning">
                                <strong>Warning:</strong> {cpmResult.warning}
                            </div>
                        )}

                        {/* Tab Navigation */}
                        <div className="cpm-tabs-container">
                            <Nav variant="tabs" className="cpm-tab-nav" activeKey={cpmActiveTab} onSelect={(tab) => setCpmActiveTab(tab)}>
                                <Nav.Item>
                                    <Nav.Link eventKey="tasks" className="cpm-tab-link">
                                        <i className="fa fa-tasks mr-2"></i> Tasks
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="cpm-network" className="cpm-tab-link">
                                        <i className="fa fa-sitemap mr-2"></i> CPM Network
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="critical-path" className="cpm-tab-link">
                                        <i className="fa fa-link mr-2"></i> Critical Path
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="milestones" className="cpm-tab-link">
                                        <i className="fa fa-flag-checkered mr-2"></i> Milestones
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>

                            {/* Tab Content */}
                            <div className="cpm-tab-content">
                                {cpmActiveTab === "tasks" && renderTasksTab()}
                                {cpmActiveTab === "cpm-network" && renderCPMNetworkTab()}
                                {cpmActiveTab === "critical-path" && renderCriticalPathTab()}
                                {cpmActiveTab === "milestones" && renderMilestonesTab()}
                            </div>
                        </div>

                        <div className="project-onboard-actions">
                            <button type="button" className="project-onboard-primary wide" onClick={finishProject}>
                                Finish Project →
                            </button>
                        </div>
                    </section>
                )}

                {currentStep === transitionStepNumber && (
                    <section className="project-onboard-transition">
                        <div className="project-onboard-transition-icon">FLOOR</div>
                        <h2>Floor {currentFloorIndex} Estimation Complete!</h2>
                        <p>
                            All selected structural modules have been estimated for Floor {currentFloorIndex}. Continue to configure
                            drawing modules for Floor {currentFloorIndex + 1}.
                        </p>
                        <button
                            type="button"
                            className="project-onboard-primary"
                            onClick={() => {
                                setCurrentFloorIndex(prev => prev + 1);
                                setCurrentStep(1);
                                setStepState("upload");
                                setSelectedDrawingTypeIds(prev => prev.filter(id => id !== "foundation" && id !== "plinth-beam"));
                            }}
                        >
                            {`Configure Floor ${currentFloorIndex + 1}`} {String.fromCharCode(8594)}
                        </button>
                    </section>
                )}
            </main>
        </div>
    );
}